import React, { useState, useRef, useMemo, useEffect } from "react";
import "./TableView.scss";
import FoldButton from "../FoldButton/FoldButton";
import CheckList from "../CheckList/CheckList";
import InfoButton from "../InfoButton/InfoButton";
import DetailsModal from "../DetailsModal/DetailsModal";
import { can, PERM, normalizeRole } from "../../config/roles";
import DeleteButton from "../DeleteButton/DeleteButton";
import AddTodoModal from "../Update/AddTodoModal/AddTodoModal";
import AddChecklistModal from "../Update/AddChecklistModal/AddChecklistModal";
import AddButton from "../AddButton";
import Toast from "../Toast/Toast";
import TooltipPortal from "../TooltipPortal/TooltipPortal";
import CustomTooltip from "../CustomTooltip/CustomTooltip";
import InputField from "../../components/InputComponent/InputField";
import PriorityBadge from "./Prioritybadge";

import DeleteWarningModal from "../DeleteWarningModal/DeleteWarningModal";
// add this near your other imports (top of file)
import {
  buildTaskPayload,
  createTasks,
  validateTask,
} from "../../services/taskService";

const isAlreadyExistsError = (res) =>
  res?.status === 409 &&
  typeof res?.message === "string" &&
  res.message.toLowerCase().includes("already exists");

export default function TableView({
  tasks = [],
  stages = [],
  setTasks,
  filters,
  onDeleteTask = null,
  canDeleteTasks = false,
  context,
  refreshProject,
  onDeleteTodo = null,
  onUpdateTask = null,
  onUpdateTodo = null,
  onUpdateTodoList = null,
  onUpdateChecklistRemark = null,
  canChangeTodoStageFor = null,
  onChangeTaskStage = null,
  onChangeTodoStage = null,
  onToggleChecklist = null,
  onCreateTodo,
  onCreateTodoList,
  projectMembers = [],
  currentAccessId = null,
  project = {},
  onOpenTodoRemarkModal = null,
  canUpdateTask = false,
  canUpdateTodo = false,
  canUpdateTodoList = false,
  currentUserRole = null,
  stageChangeLoading = {},
  onCreateTask = null,
  onOpenTaskModal = () => {},
  handleUpdateTask,
  handleUpdateTodo,
  onDeleteTodoList,
  currentUserId = null,
}) {
  const [expanded, setExpanded] = useState({});
  const [listEdits, setListEdits] = useState({});
  const [checklistActionsOpen, setChecklistActionsOpen] = useState({});
  const [defaultRowVisible, setDefaultRowVisible] = useState(false);

  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // animate-out removal to avoid layout jump
  const roleLike = normalizeRole(currentUserRole);
  const shouldSendAccessId = roleLike !== "DOER";

  // The compact-row overflow is deliberately presentation-only: it simply
  // reveals the existing controls, preserving their handlers and permissions.
  useEffect(() => {
    const closeOverflowMenus = () => {
      document
        .querySelectorAll(".mobile-more-checkbox:checked")
        .forEach((checkbox) => {
          checkbox.checked = false;
        });
    };
    const closeOnOutsideClick = (event) => {
      if (
        !event.target.closest(".row.task-row, .row.todo-row") &&
        !event.target.closest(".dropdown-portal-menu")
      ) {
        closeOverflowMenus();
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") closeOverflowMenus();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  // NOTIFY SIDEBAR IMMEDIATELY
  const emitProjectUpdate = (projectId) => {
    window.dispatchEvent(
      new CustomEvent("project-updated", { detail: { projectId } }),
    );
    console.log("[TV] dispatched project-updated", projectId);
  };

  const titleExists = (title) => {
    if (!title || !title.trim()) return false;
    const t = title.trim().toLowerCase();
    return allTasks.some(
      (task) =>
        String(task.taskTitle || "")
          .trim()
          .toLowerCase() === t,
    );
  };

  const [, setRemovingIds] = useState(new Set());

  // mark an id as removing (returns a promise resolved after animation timeout)
  const markRemoving = (id, { animationMs = 260 } = {}) =>
    new Promise((resolve) => {
      setRemovingIds((s) => new Set([...Array.from(s), String(id)]));

      // if currently expanded, collapse first to avoid sudden nested height collapse
      const key = makeExpandKey("task", id);
      setExpanded((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        next[key] = false;
        return next;
      });

      // resolve after animation length
      window.setTimeout(() => {
        setRemovingIds((s) => {
          const n = new Set(
            Array.from(s).filter((x) => String(x) !== String(id)),
          );
          return n;
        });
        resolve();
      }, animationMs);
    });

  // ---------- Fallbacks for quick-add ----------

  // Example shape: { type: "task" | "todo" | "checklist", id: number }

  const role = normalizeRole(currentUserRole);
  // ---------- Inline quick-add rows (new) ----------
  const [inlineRows, setInlineRows] = useState([]); // newest first (index 0)
  const inlineRef = useRef(null);

  const makeInlineRow = () => ({
    id: Date.now() + Math.random(),
    title: "",
    remark: "",
    status: "idle",
  });
  const [defaultRow, setDefaultRow] = React.useState(() => makeInlineRow());
  // ---------------- Inline add-multiple state ----------------
  // which task(s) currently show the persistent inline add box
  const [inlineAddOpen, setInlineAddOpen] = useState({}); // { [taskId]: true }

  const openInlineAdd = (taskId) =>
    setInlineAddOpen((s) => ({ ...s, [taskId]: true }));

  const closeInlineAdd = (taskId) =>
    setInlineAddOpen((s) => {
      const n = { ...s };
      delete n[taskId];
      return n;
    });

  // local helper: animate + call parent onDeleteTask (if provided)
  const handleLocalDeleteTask = async (taskId) => {
    if (!taskId) return;
    try {
      // first mark for animation (collapses row visually)
      await markRemoving(taskId, { animationMs: 260 });

      // now do actual state removal (optimistic). We keep parent handlers consistent:
      // If parent supplied an onDeleteTask prop that performs the API call,
      // call it here after the UI animation, but remove from UI immediately now.
      setTasks((prev) =>
        (prev || []).filter((t) => Number(t.taskId ?? t.id) !== Number(taskId)),
      );

      // also update project in TableView's local copy if present
      // (some parent calls already do this; keep consistent)
      // call parent's onDeleteTask (returns Promise)
      if (typeof onDeleteTask === "function") {
        try {
          await onDeleteTask(taskId);
        } catch (err) {
          // If parent delete failed: re-fetch / rollback
          console.error("onDeleteTask failed:", err);
          // attempt light refresh if refreshProject provided
          if (typeof refreshProject === "function") {
            try {
              await refreshProject(project?.projectId);
            } catch (e) {
              console.error("refreshProject failed after delete error:", e);
            }
          }
        }
      }
    } catch (err) {
      console.error("handleLocalDeleteTask error:", err);
    }
  };
  // note: todoInputs and updateTodoInput should already exist from previous steps
  // const [todoInputs, setTodoInputs] = useState({});
  // const updateTodoInput = (taskId, value) => setTodoInputs(s => ({ ...s, [taskId]: value }));
  const openChecklistInput = (todoId) => {
    setChecklistVisible((s) => ({ ...s, [todoId]: true }));

    // focus input after render
    setTimeout(() => {
      document.getElementById(`inline-checklist-${todoId}`)?.focus?.();
    }, 20);
  };

  const addInlineRow = () => {
    setInlineRows((r) => [makeInlineRow(), ...r]);
    setTimeout(() => {
      const inputs = inlineRef.current?.querySelectorAll(
        "input[name='inlineTitle']",
      );
      if (inputs?.length) inputs[0].focus();
    }, 30);
  };
  const removeInlineRow = (idx) => {
    setInlineRows((prev) => {
      const copy = [...prev];
      copy.splice(idx, 1);
      return copy;
    });
  };

  const updateInline = (idx, field, value) =>
    setInlineRows((r) => {
      const c = [...r];
      c[idx][field] = value;
      return c;
    });
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;

      // Close modal if open
      if (selectedItem) {
        setSelectedItem(null);
        return;
      }

      // Cancel inline row if focus is inside inlineRef
      if (
        inlineRef.current &&
        inlineRef.current.contains(document.activeElement)
      ) {
        const rows = Array.from(
          inlineRef.current.querySelectorAll(".inline-row"),
        );
        const idx = rows.findIndex((row) =>
          row.contains(document.activeElement),
        );
        if (idx >= 0) removeInlineRow(idx);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedItem, inlineRows]);

  const saveInlineRow = async (idx) => {
    const row = inlineRows[idx];
    if (!row) return;
    try {
      validateTask(row); // throws if invalid
      setInlineRows((prev) => {
        const c = [...prev];
        c[idx].status = "saving";
        return c;
      });
      // derive accessId from project members (same logic as QuickAddModal)
      const derivedAccessId =
        project?.members?.find(
          (m) => m.empId === currentUserId || m.employeeId === currentUserId,
        )?.accessId ??
        project?.accessId ??
        currentAccessId ??
        null;

      // use task-start = today, task-end = today + 2 days
      const todayYMD = new Date().toISOString().slice(0, 10);
      const twoDaysYMD = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10);

      const payload = [
        buildTaskPayload(
          { title: row.title, remark: row.remark },
          {
            projectId: project?.projectId,
            stagePositionId:
              stages?.[0]?.stagePositionResponseDTOList?.[0]?.stagePositionId ??
              null,
            startDate: todayYMD,
            endDate: twoDaysYMD,
            ...(shouldSendAccessId && { accessId: derivedAccessId }),
          },
        ),
      ];

      console.log(
        "QuickInline payload:",
        payload,
        "using accessId:",
        derivedAccessId,
      );
      const res = await createTasks(payload);
      // assume you have refreshProject in props signature (destructure at top)
      if (res?.data && res.data.length > 0) {
        // update UI quickly (existing logic)
        setInlineRows((prev) => [
          makeInlineRow(),
          ...prev.filter((_, i) => i !== idx),
        ]);

        // --- NEW: refresh the whole project data from backend (keeps UI consistent) ---
        // 🔥 FORCE project status refresh (project-level)
        if (typeof refreshProject === "function") {
          await refreshProject(project?.projectId);
        } else if (project?.projectId) {
          // fallback: hard reload project
          window.dispatchEvent(
            new CustomEvent("project-updated", {
              detail: { projectId: project.projectId },
            }),
          );
        }

        // optional: call onCreateTask/res handler as well
        if (typeof onCreateTask === "function") onCreateTask(res.data);
      }

      setInlineRows((prev) => {
        // mark row done and insert fresh blank on top
        const c = [...prev];
        c[idx].status = "done";
        // remove the saved row from list and prepend a fresh blank
        const remaining = c.filter((_, i) => i !== idx);
        return [makeInlineRow(), ...remaining];
      });
      // optionally inform parent (if you have a prop)
      if (typeof onCreateTask === "function") onCreateTask(res.data || []);
      // focus new top input
      setTimeout(() => {
        const inputs = inlineRef.current?.querySelectorAll(
          "input[name='inlineTitle']",
        );
        if (inputs?.length) inputs[0].focus();
      }, 30);
    } catch (err) {
      console.error("Inline save failed", err);

      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to create task";

      setToast({
        show: true,
        type: err?.response?.status === 409 ? "warning" : "error",
        message: apiMessage,
      });

      setInlineRows((prev) => {
        const c = [...prev];
        if (c[idx]) c[idx].status = "error";
        return c;
      });
    }
  };

  // inline checklist input per todo (keyed by todoId)
  const [checklistInputs, setChecklistInputs] = useState({});
  const updateChecklistInput = (todoId, value) =>
    setChecklistInputs((s) => ({ ...s, [todoId]: value }));

  // inline checklist remark input per todo (keyed by todoId)
  const [checklistRemarkInputs, setChecklistRemarkInputs] = useState({});
  const updateChecklistRemarkInput = (todoId, value) =>
    setChecklistRemarkInputs((s) => ({ ...s, [todoId]: value }));

  // ---------- Checklist item remark / approve (existing checklist items) ----------
  const [checklistRemarkPrompt, setChecklistRemarkPrompt] = useState(null); // { toDoListId, title, completion }
  const [checklistRemarkText, setChecklistRemarkText] = useState("");

  const openChecklistRemarkPrompt = (li) => {
    if (!li) return;
    setChecklistRemarkText(li.remark || "");
    setChecklistRemarkPrompt({
      toDoListId: li.toDoListId ?? li.id,
      title: li.toDoListTitle ?? "Checklist item",
      completion: Boolean(
        li?.completion ??
        li?.completionFlag ??
        (li?.status && String(li.status).toUpperCase() === "DONE"),
      ),
    });
  };

  const cancelChecklistRemarkPrompt = () => {
    setChecklistRemarkPrompt(null);
    setChecklistRemarkText("");
  };

  const confirmChecklistRemarkPrompt = async () => {
    if (!checklistRemarkPrompt) return;
    const { toDoListId, completion } = checklistRemarkPrompt;
    try {
      await onUpdateChecklistRemark?.(toDoListId, {
        completionFlag: completion,
        remark: checklistRemarkText,
      });
    } finally {
      setChecklistRemarkPrompt(null);
      setChecklistRemarkText("");
    }
  };

  const handleApproveChecklistItem = (li) => {
    const toDoListId = li?.toDoListId ?? li?.id;
    if (!toDoListId) return;
    onUpdateChecklistRemark?.(toDoListId, {
      completionFlag: true,
      remark: li?.remark ?? "",
    });
  };

  const todoStageInFlightRef = useRef(new Set());
  // eslint-disable-next-line no-unused-vars
  const checklistInFlightRef = useRef(new Set());
  const [todoModal, setTodoModal] = useState({
    open: false,
    taskId: null,
    stagePositionId: null,
  });
  const [checkModal, setCheckModal] = useState({ open: false, toDoId: null });
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "info",
  });

  // ---------- Stage change remark prompt ----------
  // payload shape:
  //   task: { kind: "task", taskId, newStageId, selectedLabel, isFinal, prevStageId }
  //   todo: { kind: "todo", todoId, taskId, newStageId, prevStageId }
  const [stageRemarkPrompt, setStageRemarkPrompt] = useState(null);
  const [stageRemarkText, setStageRemarkText] = useState("");

  const openStageRemarkPrompt = (payload) => {
    setStageRemarkText("");
    setStageRemarkPrompt(payload);
  };

  const commitTaskStageChange = (
    taskId,
    newStageId,
    selectedLabel,
    isFinal,
    remark,
  ) => {
    const trimmedRemark = remark && remark.trim() ? remark.trim() : undefined;
    if (typeof onChangeTaskStage === "function") {
      onChangeTaskStage(
        taskId,
        newStageId,
        selectedLabel,
        isFinal,
        trimmedRemark,
      ).catch((e) => console.error("stage change failed", e));
      // 🔥 FIRE EVENT SO SIDEBAR UPDATES
      emitProjectUpdate(project?.projectId);
    } else if (typeof onUpdateTask === "function") {
      onUpdateTask(taskId, {
        stagePositionId: newStageId,
        ...(trimmedRemark ? { remark: trimmedRemark } : {}),
      }).catch((e) => console.error("stage update failed (fallback)", e));
    }
  };

  const commitTodoStageChange = (tid, newStageId, remark) => {
    const trimmedRemark = remark && remark.trim() ? remark.trim() : undefined;
    const call =
      typeof onChangeTodoStage === "function"
        ? onChangeTodoStage(tid, newStageId, trimmedRemark)
        : typeof onUpdateTodo === "function"
          ? onUpdateTodo(tid, {
              stagePositionId: newStageId,
              ...(trimmedRemark ? { remark: trimmedRemark } : {}),
            })
          : Promise.resolve();

    call
      .catch((e) => console.error("[TableView] todo stage change failed:", e))
      .finally(() => todoStageInFlightRef.current.delete(tid));
    // 🔥 notify sidebar too
    emitProjectUpdate(project?.projectId);
  };

  const confirmStageRemark = () => {
    if (!stageRemarkPrompt) return;
    const { kind, taskId, newStageId, selectedLabel, isFinal, todoId } =
      stageRemarkPrompt;
    if (kind === "task") {
      commitTaskStageChange(
        taskId,
        newStageId,
        selectedLabel,
        isFinal,
        stageRemarkText,
      );
    } else if (kind === "todo") {
      commitTodoStageChange(todoId, newStageId, stageRemarkText);
    }
    setStageRemarkPrompt(null);
    setStageRemarkText("");
  };

  const cancelStageRemark = () => {
    if (!stageRemarkPrompt) return;
    const { kind, taskId, todoId, prevStageId } = stageRemarkPrompt;
    // revert the optimistic stage update since the change was cancelled
    if (kind === "task") {
      setTasks((prev) =>
        prev.map((t) =>
          t.taskId === taskId ? { ...t, stagePositionId: prevStageId } : t,
        ),
      );
    } else if (kind === "todo") {
      setTasks((prev) =>
        prev.map((t) =>
          t.taskId === taskId
            ? {
                ...t,
                todoRequestDTOList: (t.todoRequestDTOList || []).map((td) =>
                  (td.toDoId ?? td.id) === todoId
                    ? { ...td, stagePositionId: prevStageId }
                    : td,
                ),
              }
            : t,
        ),
      );
      todoStageInFlightRef.current.delete(todoId);
    }
    setStageRemarkPrompt(null);
    setStageRemarkText("");
  };
  // stable keys for expanded state to avoid id collisions between tasks/todos/checklists
  const makeExpandKey = (type, id) => `${type}:${String(id)}`;

  const toggleExpandKey = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // date formatter
  const fmt = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    if (isNaN(date)) return String(d);
    return date.toLocaleDateString();
  };
  const toggleExpand = (id) =>
    setExpanded((prev) => {
      const opening = !prev[id];
      const next = { ...prev, [id]: opening };
      if (opening) {
        // ensure inline checklist input is visible when todo opens
        setChecklistVisible((s) => ({ ...s, [id]: true }));
      }
      return next;
    });

  // === Helpers ===
  // Same "done" rule used by the checklist rows below.
  const isChecklistDone = (li) =>
    Boolean(
      li?.completion ??
      li?.completionFlag ??
      (li?.status && String(li.status).toUpperCase() === "DONE"),
    );

  // Pending checklists first, completed ones at the bottom.
  // Copy + stable sort, so the original order is kept within each group.
  const sortChecklistsPendingFirst = (lists = []) =>
    [...lists].sort(
      (a, b) => Number(isChecklistDone(a)) - Number(isChecklistDone(b)),
    );

  const getTodoChecklistProgress = (todo) => {
    const list = todo?.toDoListResponseDTOList || [];
    const total = list.length;
    const done = list.filter(
      (li) =>
        li?.completion ||
        li?.completionFlag ||
        (li?.status && String(li.status).toUpperCase() === "DONE"),
    ).length;
    return { done, total };
  };
  // Map each user to a stable color bucket 1..5 (same every render)
  // eslint-disable-next-line no-unused-vars
  const getAvatarColor = (idOrName) => {
    const str = String(idOrName ?? "");
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return (Math.abs(hash) % 5) + 1;
  };

  const isStageFinalById = (stagePositionId) => {
    if (!stagePositionId) return false;
    for (const s of stages || []) {
      for (const pos of s.stagePositionResponseDTOList || []) {
        if (String(pos.stagePositionId) === String(stagePositionId)) {
          // accept multiple possible field names from backend:
          // prefer explicit booleans on the position object
          const finalVal =
            pos.finalPosition ??
            pos.isFinal ??
            pos.final ?? // your backend uses `final`
            s.finalPosition ??
            s.isFinal ??
            s.final ??
            false;
          return finalVal === true || String(finalVal).toLowerCase() === "true";
        }
      }
    }
    return false;
  };
  const createChecklistInline = async (todo, title, remark) => {
    if (!title || !title.trim()) return;

    try {
      const payload = {
        toDoId: Number(todo.toDoId ?? todo.id),
        toDoListTitle: title,
        remark: remark && remark.trim() ? remark.trim() : null,
      };

      const res = await onCreateTodoList?.(payload);

      /* 🚫 DUPLICATE CHECKLIST ITEM */
      if (isAlreadyExistsError(res)) {
        setToast({
          show: true,
          type: "warning",
          message: res.message || "Checklist item already exists",
        });
        return; // ⛔ STOP HERE
      }

      /* ✅ SUCCESS ONLY */
      if (res) {
        await refreshProject?.(project?.projectId);
      }
    } catch (err) {
      setToast({
        show: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to add checklist item",
      });
    }
  };

  // assume you have currentUserId (or creatorEmpId) and project.members array
  const derivedAccessId =
    project?.members?.find(
      (m) => m.empId === currentUserId || m.employeeId === currentUserId,
    )?.accessId ??
    project?.accessId ?? // fallback if project has a default accessId
    currentAccessId ?? // another fallback if you already pass it
    null;

  // Replace existing openDetailsModal with this:
  const openDetailsModal = (item, parentTask = null) => {
    let itemToPass = item ?? {};

    // determine parent task owner from item or supplied parentTask
    const parentOwner =
      item?.parentTaskOwnerId ??
      parentTask?.taskOwnerId ??
      parentTask?.ownerId ??
      null;

    if (item?.toDoId) {
      // normalize To-Do owner id from common fields
      const todoOwnerId =
        item.assignedToEmpId ??
        item.assignedEmpId ??
        item.assigneeId ??
        item.userId ??
        item.assignedTo ??
        null;

      itemToPass = {
        ...itemToPass,
        userId: todoOwnerId,
        parentTaskOwnerId: parentOwner,
      };
    } else if (item?.taskId) {
      // normalize Task owner id
      const taskOwnerId =
        item.taskOwnerId ?? item.ownerId ?? item.taskOwnerEmpId ?? null;

      itemToPass = {
        ...itemToPass,
        taskOwnerId,
        parentTaskOwnerId: parentOwner,
      };
    }

    // use existing state setter the rest of the component uses
    setSelectedItem(itemToPass);
  };
  const MS_PER_DAY = 86400000;

  const getOverdueDays = (endDate) => {
    if (!endDate) return 0;

    const end = new Date(endDate);
    const today = new Date();

    return Math.floor((today - end) / MS_PER_DAY);
  };

  const isTodoCompleted = (todo) => {
    if (!todo) return false;
    if (isStageFinalById(todo.stagePositionId)) return true;
    const { done, total } = getTodoChecklistProgress(todo);
    return total > 0 && done === total;
  };

  // eslint-disable-next-line no-unused-vars
  const getTaskTodoProgress = (task) => {
    const todos = task?.todoRequestDTOList || [];
    const total = todos.length;
    const done = todos.filter(isTodoCompleted).length;
    return { done, total };
  };

  const calcProgress = (start, end) => {
    if (!start || !end) return { progress: 0, isOverdue: false };
    const startDate = new Date(start);
    const endDate = new Date(end);
    const today = new Date();

    const total = endDate - startDate;
    const elapsed = today - startDate;

    if (total <= 0) return { progress: 0, isOverdue: today > endDate };

    let pct = elapsed / total;
    if (pct < 0) pct = 0;
    if (pct > 1) pct = 1;

    return { progress: pct, isOverdue: today > endDate };
  };
  const applyFilters = (list) => {
    let result = [...list];

    // 🔍 Search (title + remark)
    if (filters.q?.trim()) {
      const q = filters.q.toLowerCase();

      result = result.filter((t) => {
        const title = String(t.taskTitle ?? "").toLowerCase();
        const remark = String(t.remark ?? "").toLowerCase();

        const ownerName = String(t.taskOwnerName ?? "").toLowerCase();
        const assignedName = String(t.assignedEmpName ?? "").toLowerCase();
        const createdBy = String(t.createdByName ?? "").toLowerCase();

        return (
          title.includes(q) ||
          remark.includes(q) ||
          ownerName.includes(q) ||
          assignedName.includes(q) ||
          createdBy.includes(q)
        );
      });
    }

    // 🔃 Sort
    if (filters.sort === "oldest") {
      result.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    } else {
      result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    return result;
  };

  const stageOptions = useMemo(() => {
    const byId = new Map();
    for (const s of stages || []) {
      for (const pos of s.stagePositionResponseDTOList || []) {
        const id = String(pos.stagePositionId ?? pos.id ?? "");
        if (!id || byId.has(id)) continue;
        const label =
          s.stageName ??
          s.stageTitle ??
          s.title ??
          `Stage ${pos.stagePositionId}`;
        const flag = s.stageFlag ?? s.flag ?? s.stageFlagType ?? null;
        const finalPos =
          pos.finalPosition ??
          pos.isFinal ??
          pos.final ??
          s.finalPosition ??
          s.isFinal ??
          s.final ??
          false;
        byId.set(id, {
          value: pos.stagePositionId,
          label,
          flag,
          finalPosition: finalPos,
        });
      }
    }
    return Array.from(byId.values());
  }, [stages]);

  const optimisticSetTasks = (updater) => {
    setTasks((prev) => {
      try {
        return updater(prev || []);
      } catch (e) {
        console.error("optimisticSetTasks error", e);
        return prev;
      }
    });
  };

  // eslint-disable-next-line no-unused-vars
  const saveTaskTitle = async (taskId, newTitle) => {
    if (!canUpdateTask) return;
    const id = Number(taskId);
    const previous = tasks;
    optimisticSetTasks((prev) =>
      prev.map((t) =>
        Number(t.taskId ?? t.id) === id ? { ...t, taskTitle: newTitle } : t,
      ),
    );
    try {
      if (typeof onUpdateTask === "function") {
        await onUpdateTask(taskId, { taskTitle: newTitle });
      }
    } catch (err) {
      console.error("saveTaskTitle failed:", err);
      setTasks(previous);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const saveTodoTitle = async (todoId, newTitle) => {
    if (!canUpdateTodo) return;
    const id = Number(todoId);
    const previous = tasks;
    optimisticSetTasks((prev) =>
      prev.map((task) => {
        if (!Array.isArray(task.todoRequestDTOList)) return task;
        return {
          ...task,
          todoRequestDTOList: task.todoRequestDTOList.map((td) =>
            Number(td.toDoId ?? td.id) === id
              ? { ...td, toDoTitle: newTitle }
              : td,
          ),
        };
      }),
    );
    try {
      if (typeof onUpdateTodo === "function") {
        await onUpdateTodo(todoId, { toDoTitle: newTitle });
      }
    } catch (err) {
      console.error("saveTodoTitle failed:", err);
      setTasks(previous);
    }
  };

  const saveChecklistTitle = async (todolistId, newTitle) => {
    if (!canUpdateTodoList) return;
    const id = Number(todolistId);
    const previous = tasks;
    optimisticSetTasks((prev) =>
      prev.map((task) => {
        if (!Array.isArray(task.todoRequestDTOList)) return task;
        return {
          ...task,
          todoRequestDTOList: task.todoRequestDTOList.map((td) => {
            if (!Array.isArray(td.toDoListResponseDTOList)) return td;
            return {
              ...td,
              toDoListResponseDTOList: td.toDoListResponseDTOList.map((li) =>
                Number(li.toDoListId ?? li.id) === id
                  ? { ...li, toDoListTitle: newTitle }
                  : li,
              ),
            };
          }),
        };
      }),
    );
    try {
      if (typeof onUpdateTodoList === "function") {
        await onUpdateTodoList(todolistId, { toDoListTitle: newTitle });
      }
    } catch (err) {
      console.error("saveChecklistTitle failed:", err);
      setTasks(previous);
    } finally {
      setListEdits((s) => {
        const n = { ...s };
        delete n[todolistId];
        return n;
      });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const parentTaskOwnerId =
    selectedItem?.__parent?.taskOwnerId ??
    selectedItem?.__parent?.ownerId ??
    null;

  const startEditingList = (listId, current) =>
    setListEdits((s) => ({ ...s, [listId]: current }));
  // eslint-disable-next-line no-unused-vars
  const getPriorityValue = (obj = {}) => obj.priority ?? null;

  // eslint-disable-next-line no-unused-vars
  const PRIORITY_EMOJI = { high: "", medium: "", low: "" };

  const handleKeyDownSave = (e, saveFn) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveFn();
    }
    if (e.key === "Escape") e.preventDefault();
  };

  const allTasks = [...tasks].sort((a, b) => {
    const aDone = isStageFinalById(a.stagePositionId) ? 1 : 0;
    const bDone = isStageFinalById(b.stagePositionId) ? 1 : 0;
    return aDone - bDone;
  });
  // compute today at start-of-day for robust comparisons
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Ongoing = started on or before today AND not final/completed
  const ongoingTasks = allTasks.filter((t) => {
    if (isStageFinalById(t.stagePositionId)) return false;
    const sd = t.startDate ? new Date(t.startDate) : null;
    if (!sd) return true; // no start date => treat as ongoing
    sd.setHours(0, 0, 0, 0);
    return sd <= today;
  });

  // Upcoming = start date is in the future and not completed
  const upcomingTasks = allTasks.filter((t) => {
    if (isStageFinalById(t.stagePositionId)) return false;
    const sd = t.startDate ? new Date(t.startDate) : null;
    if (!sd) return false; // no start date => not upcoming
    sd.setHours(0, 0, 0, 0);
    return sd > today;
  });

  // Completed = final/completed stage (as before)
  const completedTasks = allTasks.filter((t) =>
    isStageFinalById(t.stagePositionId),
  );
  const filteredOngoingTasks = applyFilters(ongoingTasks);
  const filteredUpcomingTasks = applyFilters(upcomingTasks);
  const filteredCompletedTasks = applyFilters(completedTasks);

  // Cancel default row -> reset to blank
  const cancelDefaultRow = () => {
    setDefaultRow(makeInlineRow());
    // optionally shift focus away
    if (inlineRef.current) inlineRef.current.blur?.();
  };

  // submit handler for default row (re-uses your existing saveInlineRow logic if you have it)
  // This wrapper either calls saveInlineRow with a new inline row, or directly invokes same creation used there.
  const submitDefaultRow = async () => {
    const row = defaultRow;
    if (!row || !row.title?.trim()) return; // don't submit empty title

    // if you already have saveInlineRow that expects index in inlineRows,
    // we can push this row into inlineRows and call saveInlineRow(0),
    // but simpler: call same save logic here (replicate minimal create behavior).
    try {
      validateTask(row); // reuse validation if available
    } catch (err) {
      // validation failed — early return (you can show toast if needed)
      return;
    }

    // optimistic UI: set status to saving
    setDefaultRow((s) => ({ ...s, status: "saving" }));

    // derive accessId same as saveInlineRow uses (copy your logic)
    const derivedAccessId =
      project?.members?.find(
        (m) => m.empId === currentUserId || m.employeeId === currentUserId,
      )?.accessId ??
      project?.accessId ??
      currentAccessId ??
      null;

    // use task-start = today, task-end = today + 2 days
    const todayYMD = new Date().toISOString().slice(0, 10);
    const twoDaysYMD = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);

    const payload = [
      buildTaskPayload(
        { title: row.title, remark: row.remark },
        {
          projectId: project?.projectId,
          stagePositionId:
            stages?.[0]?.stagePositionResponseDTOList?.[0]?.stagePositionId ??
            null,
          startDate: todayYMD,
          endDate: twoDaysYMD,
          ...(shouldSendAccessId && { accessId: derivedAccessId }),
        },
      ),
    ];
    // DUPLICATE CHECK - default row
    if (titleExists(row.title)) {
      const msg = `Task "${String(row.title).trim()}" already exists.`;
      // reuse TableView's toast state
      setToast({ show: true, type: "warning", message: msg });
      // reset status so UI doesn't show 'saving'
      setDefaultRow((s) => ({ ...s, status: "idle" }));
      return;
    }

    try {
      const res = await createTasks(payload);
      // if success, refresh UI / project (same as your saveInlineRow does)
      if (res?.data && res.data.length > 0) {
        if (typeof refreshProject === "function") {
          await refreshProject(project?.projectId);
        }
        setDefaultRow(makeInlineRow());
      } else {
        setDefaultRow((s) => ({ ...s, status: "error" }));
      }
    } catch (e) {
      console.error("[submitDefaultRow] createTasks failed:", e);

      const apiMessage =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to create task";

      setToast({
        show: true,
        type: e?.response?.status === 409 ? "warning" : "error",
        message: apiMessage,
      });

      setDefaultRow((s) => ({ ...s, status: "error" }));
    }
  };
  React.useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;

      if (selectedItem) {
        setSelectedItem(null);
        return;
      }

      // If focus inside inlineRef -> remove that inline row (existing behavior)
      if (
        inlineRef.current &&
        inlineRef.current.contains(document.activeElement)
      ) {
        const rows = Array.from(
          inlineRef.current.querySelectorAll(".inline-row"),
        );
        const idx = rows.findIndex((row) =>
          row.contains(document.activeElement),
        );
        if (idx >= 0) {
          removeInlineRow(idx);
          return;
        }
      }

      // If defaultRow input is focused => cancel defaultRow
      const defTitle = document.getElementById(`default-inline-title`);
      const defRemark = document.getElementById(`default-inline-remark`);
      if (
        defTitle === document.activeElement ||
        defRemark === document.activeElement
      ) {
        cancelDefaultRow();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // cancelDefaultRow is a plain function re-created every render (not
    // memoized); adding it here would re-subscribe this listener on every
    // render instead of only when selectedItem/inlineRows/defaultRow change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, inlineRows, defaultRow]);

  // inline todo input per task (keyed by taskId)
  const [todoInputs, setTodoInputs] = useState({});
  // inline checklist input visibility per todo (true = input shown)
  const [checklistVisible, setChecklistVisible] = useState({});

  // helper: update single input
  const updateTodoInput = (taskId, value) =>
    setTodoInputs((s) => ({ ...s, [taskId]: value }));

  const createTodoInline = async (task, title) => {
    if (!title || !title.trim()) return;

    const taskId = task.taskId ?? task.id;

    try {
      const payload = {
        toDoTitle: title,
        remark: null,
        priority: "MEDIUM",
        taskId: Number(taskId),
        stagePositionId: 1,
        assignedEmpId: currentUserId ? Number(currentUserId) : null,
        accessId: derivedAccessId ?? null,
      };

      const res = await onCreateTodo?.(payload);

      if (
        res?.status === 409 ||
        res?.message?.toLowerCase?.().includes("already exists")
      ) {
        setToast({
          show: true,
          type: "warning",
          message: res.message,
        });
        return;
      }

      await refreshProject?.(project?.projectId);
    } catch (err) {
      setToast({
        show: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to create ToDo",
      });
    }
  };

  useEffect(() => {
    if (ongoingTasks.length === 0) {
      setDefaultRowVisible(true);
    }
  }, [ongoingTasks.length]);

  // ---------------- Render ----------------
  return (
    <div className="table-view">
      {toast?.show && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast((t) => ({ ...t, show: false }))}
        />
      )}

      {/* Header row visible for both sections */}
      <div className="table-header">
        <div className="col title">Stages / Task / ToDo</div>

        {/* <div className="col progressnumber ">Progress</div> */}
        <div className="col owner">Owner</div>
        <div className="col timeline">Timeline</div>
        <div className="col priority">Priority</div>
        <div className="col details">Details</div>
        <div className="col stage">Status</div>
        <div className="col stage">Add</div>
        <div className="col actions">Actions</div>
      </div>
      {/* ---------- Inline quick-add rows (new) ---------- */}
      {/* ---------- Inline quick-add rows ---------- */}
      {/* ---------- Inline quick-add rows (new) ---------- */}
      <div ref={inlineRef} className="inline-quick-add">
        {defaultRowVisible && inlineRows.length === 0 ? (
          // Default single quick-add row when no ongoing tasks
          <div className="row inline-row default-inline-row" key="default">
            <div className="col title">
              <InputField
                id={`default-inline-title`}
                label=""
                as="input"
                type="text"
                name="inlineTitle"
                placeholder="Title- Press Enter To Save, Tab to Swtich Next"
                value={defaultRow.title}
                onChange={(e) =>
                  setDefaultRow((s) => ({ ...s, title: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    // If title enter -> focus remark
                    e.preventDefault();
                    submitDefaultRow();
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    cancelDefaultRow();
                    setDefaultRowVisible(false);
                  }
                }}
              />
            </div>

            <div className="col remark">
              <InputField
                id={`default-inline-remark`}
                label=""
                as="input"
                type="text"
                name="inlineRemark"
                placeholder="Optional remark"
                value={defaultRow.remark}
                onChange={(e) =>
                  setDefaultRow((s) => ({ ...s, remark: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitDefaultRow();
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    cancelDefaultRow();
                    setDefaultRowVisible(false);
                  }
                }}
              />
            </div>

            <div className="inline-actions-row">
              <div className="col actions submit-col">
                <button
                  type="button"
                  className="btn btn-submit-inline"
                  onClick={() => submitDefaultRow()}
                  disabled={
                    !defaultRow.title?.trim() || defaultRow.status === "saving"
                  }
                >
                  {defaultRow.status === "saving" ? "Saving…" : "Submit"}
                </button>
              </div>

              <div className="col actions inline-cancel-col">
                <button
                  className="btn btn-cancel-inline"
                  type="button"
                  onClick={() => {
                    cancelDefaultRow();
                    setDefaultRowVisible(false); // ⬅️ Hide the row
                  }}
                  title="Cancel"
                  aria-label="Cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Existing inlineRows mapping (keeps your previous behavior)
          inlineRows.map((row, i) => (
            <div className="row inline-row" key={row.id}>
              <div className="col title">
                <InputField
                  id={`inline-title-${row.id}`}
                  label=""
                  as="input"
                  type="text"
                  name="inlineTitle"
                  placeholder="Title — Tab to remark, Enter to save, Esc to exit"
                  value={row.title}
                  onChange={(e) => updateInline(i, "title", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Tab") {
                      e.preventDefault();
                      const remarks = inlineRef.current?.querySelectorAll(
                        "input[name='inlineRemark']",
                      );
                      remarks?.[i]?.focus();
                    }
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (!row.title?.trim()) return;
                      saveInlineRow(i);
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      removeInlineRow(i);
                    }
                  }}
                />
              </div>

              <div className="col remark">
                <InputField
                  id={`inline-remark-${row.id}`}
                  label=""
                  as="input"
                  type="text"
                  name="inlineRemark"
                  placeholder="Optional remark"
                  value={row.remark}
                  onChange={(e) => updateInline(i, "remark", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (!row.title?.trim()) return;
                      saveInlineRow(i);
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      removeInlineRow(i);
                    }
                  }}
                />
              </div>

              <div className="inline-actions-row">
                <div className="col actions submit-col">
                  <button
                    type="button"
                    className="btn btn-submit-inline"
                    onClick={() => saveInlineRow(i)}
                    disabled={!row.title?.trim() || row.status === "saving"}
                  >
                    {row.status === "saving" ? "Saving…" : "Submit"}
                  </button>
                </div>

                <div className="col actions inline-cancel-col">
                  <button
                    className="btn btn-cancel-inline"
                    type="button"
                    onClick={() => removeInlineRow(i)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Ongoing tasks rendering */}
      {ongoingTasks.length === 0 && upcomingTasks.length === 0 && (
        <div className="empty">No active or upcoming tasks 🎉</div>
      )}

      {filteredOngoingTasks.map((task) => {
        const taskId = task.taskId ?? task.id;

        const roleLike = normalizeRole(currentUserRole);

        // 1️⃣ Role-based flags
        const isCreatorOrApprover =
          roleLike === "CREATOR" || roleLike === "APPROVER";

        const isViewerLike = roleLike === "VIEWER" || roleLike === "CLIENT";

        // 2️⃣ Ownership (task)
        const taskOwnerId =
          task?.taskOwnerId ??
          task?.ownerId ??
          task?.taskOwnerEmpId ??
          task?.createdEmpId ??
          task?.createdBy ??
          task?.assignedEmpId ??
          null;

        const isTaskOwner =
          currentUserId != null &&
          taskOwnerId != null &&
          String(taskOwnerId) === String(currentUserId);

        // 3️⃣ Permissions that DEPEND on the above
        const canViewInfo = !isViewerLike;

        const canAddItem =
          isCreatorOrApprover || (roleLike === "DOER" && isTaskOwner);

        const canChangeStage =
          isCreatorOrApprover || (roleLike === "DOER" && isTaskOwner);

        const canAddTodo =
          isCreatorOrApprover || (roleLike === "DOER" && isTaskOwner);

        const canTaskStage =
          isCreatorOrApprover || (roleLike === "DOER" && isTaskOwner);

        // Delete rights:
        const canDeleteTask =
          can(roleLike, PERM.TASK_DELETE) &&
          (roleLike === "CREATOR" ||
            roleLike === "APPROVER" ||
            (roleLike === "DOER" && isTaskOwner));

        const taskKey = makeExpandKey("task", taskId);

        return (
          <React.Fragment key={taskId}>
            {/* === TASK ROW === */}
            <div
              className="row task-row"
              onClick={(event) => {
                if (
                  event.target.closest(
                    "button, input, label, a, [role='button']",
                  )
                )
                  return;
                event.currentTarget.querySelector(".title-toggle")?.click();
              }}
            >
              <div className="col title">
                <button
                  className="toggle-btn"
                  onClick={() => {
                    // determine whether the task section will open or close
                    const opening = !expanded[taskKey];

                    // toggle the task section (existing helper)
                    toggleExpandKey(taskKey);

                    // if we're opening the task, auto-open the FIRST todo's checklist (if present)
                    if (opening) {
                      const firstTodo = (task.todoRequestDTOList || [])[0];
                      if (firstTodo) {
                        const firstTodoId = firstTodo.toDoId ?? firstTodo.id;
                        if (firstTodoId !== undefined && firstTodoId !== null) {
                          // set the todo expand state to true (merge with existing expanded state)
                          // small timeout ensures UI already created the DOM for nested rows if you rely on focus later
                          setTimeout(() => {
                            setExpanded((prev) => ({
                              ...prev,
                              [firstTodoId]: true,
                            }));
                            // optional: focus the inline checklist input for the first todo
                            // setTimeout(() => document.getElementById(`inline-checklist-${firstTodoId}`)?.focus?.(), 20);
                          }, 10);
                        }
                      }
                    }
                  }}
                  aria-expanded={!!expanded[taskKey]}
                >
                  {expanded[taskKey] ? "▾" : "▸"}
                </button>
                {/* TITLE: clickable area that behaves like the toggle button (accessible) */}
                <div className="title-block">
                  <button
                    type="button"
                    className="title-toggle"
                    onClick={() => {
                      // same toggle behavior as the left toggle button
                      const opening = !expanded[taskKey];
                      toggleExpandKey(taskKey);

                      if (opening) {
                        const firstTodo = (task.todoRequestDTOList || [])[0];
                        if (firstTodo) {
                          const firstTodoId = firstTodo.toDoId ?? firstTodo.id;
                          if (
                            firstTodoId !== undefined &&
                            firstTodoId !== null
                          ) {
                            setTimeout(() => {
                              setExpanded((prev) => ({
                                ...prev,
                                [firstTodoId]: true,
                              }));
                            }, 10);
                          }
                        }
                      }
                    }}
                    aria-expanded={!!expanded[taskKey]}
                    onKeyDown={(e) => {
                      // handle Enter / Space like the button (space/enter should toggle)
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.currentTarget.click();
                      }
                    }}
                  >
                    <div className="main">
                      <CustomTooltip content={task.taskTitle || ""}>
                        <span className="title-text truncate">
                          {task.taskTitle}
                        </span>
                      </CustomTooltip>
                      <span className="able-badge">Stage</span>
                    </div>
                  </button>
                </div>
              </div>
              <input
                type="checkbox"
                id={`mobile-more-${taskId}`}
                className="mobile-more-checkbox"
                tabIndex={0}
              />
              <label
                htmlFor={`mobile-more-${taskId}`}
                className="mobile-more-toggle"
              >
                <span className="mobile-more-toggle-show">More</span>
                <span className="mobile-more-toggle-hide">Less</span>
              </label>

              <div className="col owner col--collapsible">
                {task.taskOwnerName ? (
                  <div className="owner-badge">
                    <span className="owner-avatar">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="#1877ebff"
                      >
                        <path
                          d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 
              1.79-4 4 1.79 4 4 4zm0 2c-2.67 
              0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                        />
                      </svg>
                    </span>
                    <span className="owner-name">{task.taskOwnerName}</span>
                  </div>
                ) : (
                  <span className="muted555 sm">—</span>
                )}
              </div>
              <div className="col timeline col--collapsible">
                {task.startDate && task.endDate ? (
                  (() => {
                    const { progress, isOverdue } = calcProgress(
                      task.startDate,
                      task.endDate,
                    );
                    return (
                      <TooltipPortal
                        label={
                          isOverdue
                            ? `${getOverdueDays(task.endDate)} days overdue`
                            : `Start: ${fmt(task.startDate)} | End: ${fmt(
                                task.endDate,
                              )} • ${Math.round(progress * 100)}%`
                        }
                        offsetY={28}
                      >
                        <div
                          className={`timeline-bar5 ${
                            isOverdue ? "overdue" : ""
                          }`}
                        >
                          {isOverdue ? (
                            <span className="overdue-text">
                              {getOverdueDays(task.endDate)} days overdue
                            </span>
                          ) : (
                            <div
                              className="timeline-fill5"
                              style={{ width: `${progress * 100}%` }}
                            />
                          )}
                        </div>
                      </TooltipPortal>
                    );
                  })()
                ) : (
                  <span className="muted555">—</span>
                )}
              </div>
              <div className="col priority col--collapsible">
                <div className="priority-cell">
                  <span
                    className={`tableview-priority-badge ${String(
                      task.priority || "medium",
                    ).toLowerCase()}`}
                  >
                    {task.priority || "Medium"}
                  </span>
                </div>
              </div>

              <div className="col details col--collapsible">
                {canViewInfo ? (
                  <span
                    className="info-btn-wrap"
                    role="button"
                    tabIndex={0}
                    onClick={() => openDetailsModal(task)}
                  >
                    <InfoButton />
                  </span>
                ) : (
                  <span
                    className="info-btn-wrap disabled"
                    title="You don't have permission to view details"
                    style={{ opacity: 0.5, cursor: "not-allowed" }}
                  >
                    <InfoButton />
                  </span>
                )}
              </div>

              <div className="col stage col--collapsible">
                <div
                  onMouseDown={(e) => {
                    if (!canTaskStage) {
                      e.preventDefault();
                      setToast({
                        show: true,
                        type: "info",
                        message:
                          "Only the task owner, Creator, or Approver can change the task stage.",
                      });
                    }
                  }}
                  title={
                    !canTaskStage
                      ? "Only the task owner, Creator, or Approver can change the task stage"
                      : ""
                  }
                >
                  <FoldButton
                    label="Stage"
                    options={stageOptions}
                    value={task.stagePositionId}
                    onSelect={(newStageId) => {
                      const prevStageId = task.stagePositionId;
                      setTasks((prev) =>
                        prev.map((t) =>
                          t.taskId === task.taskId
                            ? { ...t, stagePositionId: newStageId }
                            : t,
                        ),
                      );
                      const selected = stageOptions.find(
                        (s) => String(s.value) === String(newStageId),
                      );
                      const selectedLabel = selected?.label ?? "";
                      const flag = selected?.flag ?? "";
                      const isFinal =
                        flag && String(flag).toLowerCase().includes("final");
                      openStageRemarkPrompt({
                        kind: "task",
                        taskId,
                        newStageId,
                        selectedLabel,
                        isFinal,
                        prevStageId,
                      });
                    }}
                    disabled={!canChangeStage}
                  />
                </div>
                {!!(stageChangeLoading && stageChangeLoading[taskId]) && (
                  <span className="saving">Saving…</span>
                )}
              </div>

              <div className="col add">
                {canAddItem ? (
                  <AddButton
                    size={30}
                    tooltip="Quick add row"
                    ariaLabel="Quick add row"
                    onClick={addInlineRow}
                    text="Create Task"
                  />
                ) : (
                  <button
                    className="btn btn-disabled"
                    disabled
                    title="You don't have permission to add items"
                    style={{
                      opacity: 0.9,
                      cursor: "not-allowed",
                    }}
                  ></button>
                )}
              </div>

              <div className="col actions col--collapsible">
                {canDeleteTask ? (
                  <DeleteButton
                    size={40}
                    onClick={() =>
                      setDeleteTarget({
                        type: "task",
                        id: taskId,
                        onConfirm: () => handleLocalDeleteTask(taskId),
                      })
                    }
                  />
                ) : (
                  <button
                    className="btn btn-danger"
                    disabled
                    title="You don't have permission to delete tasks"
                    style={{ opacity: 0.6, cursor: "not-allowed" }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>

            {/* === TODOs under Task === */}
            <div
              className={`expandable expandable-task ${
                expanded[taskKey] ? "open" : ""
              }`}
            >
              {inlineAddOpen[taskId] && expanded[taskKey] && (
                <div
                  className="inline-add-persistent"
                  style={{ padding: 12, borderBottom: "1px solid #f0f0f0" }}
                >
                  <div
                    className="inline-add-controls"
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <InputField
                      id={`inline-add-${taskId}`}
                      label=""
                      as="input"
                      type="text"
                      name={`inlineAdd-${taskId}`}
                      placeholder="Type task title and press Enter (Esc to cancel)"
                      value={todoInputs[taskId] || ""}
                      onChange={(e) => updateTodoInput(taskId, e.target.value)}
                      onKeyDown={async (e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const val = (todoInputs[taskId] || "").trim();
                          if (!val) return;
                          await createTodoInline(task, val);
                          updateTodoInput(taskId, "");
                          setTimeout(
                            () =>
                              document
                                .getElementById(`inline-add-${taskId}`)
                                ?.focus?.(),
                            20,
                          );
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          updateTodoInput(taskId, "");
                          closeInlineAdd(taskId);
                        }
                      }}
                      style={{ flex: 1 }}
                    />

                    <button
                      className="btn btn-submit-inline"
                      type="button"
                      onClick={async () => {
                        const val = (todoInputs[taskId] || "").trim();
                        if (!val) return;
                        await createTodoInline(task, val);
                        updateTodoInput(taskId, "");
                        setTimeout(
                          () =>
                            document
                              .getElementById(`inline-add-${taskId}`)
                              ?.focus?.(),
                          20,
                        );
                      }}
                    >
                      Submit
                    </button>

                    <button
                      className="btn btn-cancel-inline"
                      type="button"
                      onClick={() => {
                        updateTodoInput(taskId, "");
                        closeInlineAdd(taskId);
                      }}
                      title="Cancel"
                      aria-label="Cancel"
                    >
                      Cancel
                    </button>
                  </div>

                  <div
                    className="inline-add-hint"
                    style={{ marginTop: 8, color: "#777", fontSize: 13 }}
                  >
                    Add multiple tasks quickly — press Enter to add more, Esc or
                    Cancel to close.
                  </div>
                </div>
              )}

              {expanded[taskKey] &&
                (task.todoRequestDTOList || []).length === 0 && (
                  <div className="no-todo-inline" style={{ padding: 12 }}>
                    <div
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      <InputField
                        id={`first-todo-${taskId}`}
                        label=""
                        as="input"
                        type="text"
                        name={`firstTodo-${taskId}`}
                        placeholder="Type first Task and press Enter"
                        value={todoInputs[taskId] || ""}
                        onChange={(e) =>
                          updateTodoInput(taskId, e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const val = (todoInputs[taskId] || "").trim();
                            if (val) createTodoInline(task, val);
                          }
                          if (e.key === "Escape") {
                            e.preventDefault();
                            updateTodoInput(taskId, "");
                          }
                        }}
                        style={{ flex: 1 }}
                      />
                      <button
                        className="btn btn-submit-inline"
                        type="button"
                        onClick={() => {
                          const val = (todoInputs[taskId] || "").trim();
                          if (val) createTodoInline(task, val);
                        }}
                      >
                        Submit
                      </button>
                    </div>

                    <div style={{ marginTop: 8, color: "#777", fontSize: 13 }}>
                      No Tasks yet — press Enter to create the first Task.
                    </div>
                  </div>
                )}

              {/* --- Existing todos rendering (unchanged) --- */}
              {(task.todoRequestDTOList || []).map((todo) => {
                const todoId = todo.toDoId ?? todo.id;
                const allowedForThisTodo =
                  !!canUpdateTodo &&
                  (!canChangeTodoStageFor || canChangeTodoStageFor(todo, task));

                const todoOwnerId =
                  todo?.assignedEmpId ??
                  todo?.empId ??
                  todo?.assigneeId ??
                  null;
                const isTodoOwner =
                  currentUserId != null &&
                  todoOwnerId != null &&
                  String(todoOwnerId) === String(currentUserId);

                const canTodoStage =
                  isCreatorOrApprover ||
                  (roleLike === "DOER" && (isTaskOwner || isTodoOwner));

                const canDeleteTodo =
                  can(roleLike, PERM.TODO_DELETE) &&
                  (roleLike === "CREATOR" ||
                    roleLike === "APPROVER" ||
                    (roleLike === "DOER" && (isTaskOwner || isTodoOwner)));

                const { progress: todoProgress, isOverdue: todoOverdue } =
                  calcProgress(
                    todo.startDate ?? task.startDate,
                    todo.endDate ?? task.endDate,
                  );

                return (
                  <React.Fragment key={todoId}>
                    <div
                      className={`row todo-row ${
                        todo._isNew ? "new-item" : ""
                      } ${todo._isReady ? "ready" : ""}`}
                    >
                      <div className="col title">
                        <button
                          className="toggle-btn"
                          onClick={() => {
                            toggleExpand(todoId);

                            // wait for UI to expand, then focus the input field
                            setTimeout(() => {
                              document
                                .getElementById(`inline-checklist-${todoId}`)
                                ?.focus?.();
                            }, 20);
                          }}
                          aria-expanded={!!expanded[todoId]}
                          aria-controls={`todo-checklist-${todoId}`}
                          aria-label={`${expanded[todoId] ? "Collapse" : "Expand"} subtask ${todo.toDoTitle || ""}`}
                        >
                          {expanded[todoId] ? "▾" : "▸"}
                        </button>

                        <span className="todo-title-text">
                          <CustomTooltip
                            content={todo.toDoTitle || "(no title)"}
                          >
                            <button
                              type="button"
                              className="title-toggle todo truncate"
                              onClick={() => {
                                toggleExpand(todoId);
                                setTimeout(() => {
                                  document
                                    .getElementById(
                                      `inline-checklist-${todoId}`,
                                    )
                                    ?.focus?.();
                                }, 20);
                              }}
                              aria-expanded={!!expanded[todoId]}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.currentTarget.click();
                                }
                              }}
                            >
                              {todo.toDoTitle || "(no title)"}
                            </button>
                          </CustomTooltip>
                        </span>
                        {todo.remark && (
                          <CustomTooltip content={todo.remark}>
                            <span
                              className="todo-remark-icon"
                              style={{
                                marginLeft: 6,
                                fontSize: 12,
                                color: "#6b7280",
                                cursor: "help",
                              }}
                              title="Has remark"
                            >
                              📝
                            </span>
                          </CustomTooltip>
                        )}
                        <span className="able-badge todo">Task</span>
                        <div className="col progressnumber ">
                          {(() => {
                            const { done, total } =
                              getTodoChecklistProgress(todo);
                            return total > 0 ? (
                              <span className="checklist-badge">
                                {done}/{total}
                              </span>
                            ) : (
                              "0/0"
                            );
                          })()}
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        id={`mobile-more-todo-${todoId}`}
                        className="mobile-more-checkbox"
                        tabIndex={0}
                      />
                      <label
                        htmlFor={`mobile-more-todo-${todoId}`}
                        className="mobile-more-toggle"
                      >
                        <span className="mobile-more-toggle-show">More</span>
                        <span className="mobile-more-toggle-hide">Less</span>
                      </label>

                      <div className="col owner col--collapsible">
                        {todo.assignedEmpName ? (
                          <div className="owner-badge">
                            <span className="owner-avatar">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="#1877ebff"
                              >
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                              </svg>
                            </span>
                            <span className="owner-name">
                              {todo.assignedEmpName}
                            </span>
                          </div>
                        ) : (
                          <span className="muted555 sm">—</span>
                        )}
                      </div>

                      <div className="col timeline col--collapsible">
                        <div
                          className={`timeline-bar ${
                            todoOverdue ? "overdue" : ""
                          }`}
                          title={`Start: ${
                            todo.startDate ?? task.startDate ?? "?"
                          } | End: ${todo.endDate ?? task.endDate ?? "?"}`}
                        >
                          <div
                            className="timeline-fill"
                            style={{ width: `${todoProgress * 100}%` }}
                          />
                        </div>
                      </div>

                      <div className="col priority col--collapsible">
                        <PriorityBadge priority={todo.priority} />
                      </div>

                      <div className="col details col--collapsible">
                        <span
                          className="info-btn-wrap"
                          role="button"
                          tabIndex={0}
                          onClick={() => openDetailsModal(todo, task)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openDetailsModal(todo, task);
                            }
                          }}
                        >
                          <InfoButton />
                        </span>
                      </div>

                      <div className="col stage col--collapsible">
                        <div
                          onMouseDown={(e) => {
                            if (!allowedForThisTodo || !canTodoStage) {
                              e.preventDefault();
                              setToast({
                                show: true,
                                type: "info",
                                message:
                                  "You can change stage only for your own ToDos or tasks you own.",
                              });
                            }
                          }}
                          title={
                            !allowedForThisTodo || !canTodoStage
                              ? "Stage change not allowed here"
                              : ""
                          }
                        >
                          <FoldButton
                            label="Stage"
                            options={stageOptions}
                            value={todo.stagePositionId}
                            onSelect={(newStageId) => {
                              if (
                                String(newStageId) ===
                                String(todo.stagePositionId)
                              )
                                return;
                              const tid = todoId;
                              if (todoStageInFlightRef.current.has(tid)) return;
                              todoStageInFlightRef.current.add(tid);
                              const prevStageId = todo.stagePositionId;

                              setTasks((prev) =>
                                prev.map((t) =>
                                  t.taskId === task.taskId
                                    ? {
                                        ...t,
                                        todoRequestDTOList:
                                          t.todoRequestDTOList.map((td) =>
                                            (td.toDoId ?? td.id) === tid
                                              ? {
                                                  ...td,
                                                  stagePositionId: newStageId,
                                                }
                                              : td,
                                          ),
                                      }
                                    : t,
                                ),
                              );

                              openStageRemarkPrompt({
                                kind: "todo",
                                todoId: tid,
                                taskId: task.taskId,
                                newStageId,
                                prevStageId,
                              });
                              // 🔥 notify sidebar too
                              emitProjectUpdate(project?.projectId);
                            }}
                            disabled={!allowedForThisTodo || !canTodoStage}
                          />
                        </div>
                      </div>

                      <div className="col add">
                        {can(role, PERM.TODOLIST_UPDATE) && (
                          <AddButton
                            size={28}
                            text="Create Task"
                            textPosition="right"
                            ariaLabel={`Add todo inline for task ${taskId}`}
                            onClick={(e) => {
                              if (!canAddTodo) {
                                setToast({
                                  show: true,
                                  message:
                                    "Only the task owner, Creator, or Approver can add ToDos.",
                                  type: "info",
                                });
                                return;
                              }

                              if (e.shiftKey) {
                                setTodoModal({
                                  open: true,
                                  taskId: taskId,
                                  stagePositionId: task.stagePositionId ?? null,
                                });
                                return;
                              }

                              openInlineAdd(taskId);
                              setTimeout(
                                () =>
                                  document
                                    .getElementById(`inline-add-${taskId}`)
                                    ?.focus?.(),
                                30,
                              );
                            }}
                          />
                        )}
                      </div>

                      <div className="col actions col--collapsible">
                        {canDeleteTodo && onDeleteTodo ? (
                          <DeleteButton
                            size={40}
                            onClick={() =>
                              setDeleteTarget({ type: "todo", id: todoId })
                            }
                          />
                        ) : (
                          <span
                            style={{ color: "#999", fontSize: 12 }}
                            title="You don't have permission to delete todos"
                          >
                            —
                          </span>
                        )}
                      </div>
                    </div>

                    <div
                      id={`todo-checklist-${todoId}`}
                      className={`expandable expandable-todo ${
                        expanded[todoId] ? "open" : ""
                      } ${checklistActionsOpen[todoId] ? "checklist-actions-open" : ""}`}
                    >
                      {(checklistVisible[todoId] ?? false) && (
                        <div
                          style={{
                            padding: "10px 12px",
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                            }}
                          >
                            <InputField
                              id={`inline-checklist-${todoId}`}
                              as="input"
                              label=""
                              type="text"
                              placeholder="Add checklist item — press Enter to add (Esc to hide input)"
                              value={checklistInputs[todoId] || ""}
                              onChange={(e) =>
                                updateChecklistInput(todoId, e.target.value)
                              }
                              onKeyDown={async (e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const val = (
                                    checklistInputs[todoId] || ""
                                  ).trim();
                                  if (!val) return;
                                  await createChecklistInline(
                                    todo,
                                    val,
                                    checklistRemarkInputs[todoId] || "",
                                  );
                                  updateChecklistInput(todoId, "");
                                  updateChecklistRemarkInput(todoId, "");
                                  setTimeout(
                                    () =>
                                      document
                                        .getElementById(
                                          `inline-checklist-${todoId}`,
                                        )
                                        ?.focus?.(),
                                    10,
                                  );
                                }
                                if (e.key === "Escape") {
                                  // Hide only the input — do NOT collapse the todo
                                  e.preventDefault();
                                  updateChecklistInput(todoId, "");
                                  updateChecklistRemarkInput(todoId, "");
                                  setChecklistVisible((s) => ({
                                    ...s,
                                    [todoId]: false,
                                  }));
                                }
                              }}
                              style={{ flex: 1 }}
                            />

                            <InputField
                              id={`inline-checklist-remark-${todoId}`}
                              as="input"
                              label=""
                              type="text"
                              placeholder="Remark (optional)"
                              value={checklistRemarkInputs[todoId] || ""}
                              onChange={(e) =>
                                updateChecklistRemarkInput(
                                  todoId,
                                  e.target.value,
                                )
                              }
                              onKeyDown={async (e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const val = (
                                    checklistInputs[todoId] || ""
                                  ).trim();
                                  if (!val) return;
                                  await createChecklistInline(
                                    todo,
                                    val,
                                    checklistRemarkInputs[todoId] || "",
                                  );
                                  updateChecklistInput(todoId, "");
                                  updateChecklistRemarkInput(todoId, "");
                                }
                                if (e.key === "Escape") {
                                  e.preventDefault();
                                  updateChecklistInput(todoId, "");
                                  updateChecklistRemarkInput(todoId, "");
                                  setChecklistVisible((s) => ({
                                    ...s,
                                    [todoId]: false,
                                  }));
                                }
                              }}
                            />

                            <button
                              className="btn btn-submit-inline"
                              onClick={async () => {
                                const val = (
                                  checklistInputs[todoId] || ""
                                ).trim();
                                if (!val) return;
                                await createChecklistInline(
                                  todo,
                                  val,
                                  checklistRemarkInputs[todoId] || "",
                                );
                                updateChecklistInput(todoId, "");
                                updateChecklistRemarkInput(todoId, "");
                                setTimeout(
                                  () =>
                                    document
                                      .getElementById(
                                        `inline-checklist-${todoId}`,
                                      )
                                      ?.focus?.(),
                                  10,
                                );
                              }}
                            >
                              Submit
                            </button>

                            <button
                              className="btn btn-cancel-inline"
                              type="button"
                              onClick={() => {
                                // Hide only the input (do NOT collapse the todo)
                                updateChecklistInput(todoId, "");
                                updateChecklistRemarkInput(todoId, "");
                                setChecklistVisible((s) => ({
                                  ...s,
                                  [todoId]: false,
                                }));
                              }}
                              title="Cancel"
                              aria-label="Cancel adding checklist item"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {(() => {
                        const lists = sortChecklistsPendingFirst(
                          todo.toDoListResponseDTOList || [],
                        );
                        if (!lists.length) return null;
                        return lists.map((li) => {
                          const listId = li.toDoListId ?? li.id;
                          const isDone = Boolean(
                            li?.completion ??
                            li?.completionFlag ??
                            (li?.status &&
                              String(li.status).toUpperCase() === "DONE"),
                          );
                          const displayName =
                            li.toDoListTitle?.trim?.() || "Checklist";

                          const setDone = (nextValue) => {
                            if (!canUpdateTodoList) return;
                            setTasks((prev) =>
                              (prev || []).map((t) => {
                                if (
                                  Number(t.taskId ?? t.id) !==
                                  Number(task.taskId ?? task.id)
                                )
                                  return t;
                                return {
                                  ...t,
                                  todoRequestDTOList: (
                                    t.todoRequestDTOList || []
                                  ).map((td) => {
                                    if (
                                      Number(td.toDoId ?? td.id) !==
                                      Number(todoId)
                                    )
                                      return td;
                                    return {
                                      ...td,
                                      toDoListResponseDTOList: (
                                        td.toDoListResponseDTOList || []
                                      ).map((item) => {
                                        const iKey = Number(
                                          item.toDoListId ?? item.id,
                                        );
                                        if (iKey !== Number(listId))
                                          return item;
                                        return {
                                          ...item,
                                          completion: !!nextValue,
                                          status: nextValue
                                            ? "DONE"
                                            : "NOT_DONE",
                                        };
                                      }),
                                    };
                                  }),
                                };
                              }),
                            );

                            if (typeof onToggleChecklist === "function") {
                              onToggleChecklist(listId, !!nextValue).catch(
                                (e) =>
                                  console.error("toggle checklist failed", e),
                              );
                            } else if (typeof onUpdateTodoList === "function") {
                              onUpdateTodoList(listId, {
                                completion: !!nextValue,
                              }).catch((e) =>
                                console.error(
                                  "toggle checklist (fallback) failed",
                                  e,
                                ),
                              );
                            }
                          };

                          return (
                            <div
                              className={`checklist-line ${
                                li._isNew ? "new-item" : ""
                              } ${li._isReady ? "ready" : ""}`}
                              key={listId}
                            >
                              <div className="checklist-checkbox">
                                <CheckList
                                  id={`checklist-${listId}`}
                                  label=""
                                  checked={isDone}
                                  disabled={!canUpdateTodoList}
                                  onChange={(e) =>
                                    setDone(!!e?.target?.checked)
                                  }
                                />
                              </div>

                              <div className="checklist-main">
                                {listEdits[listId] !== undefined ? (
                                  <input
                                    autoFocus
                                    value={listEdits[listId]}
                                    onChange={(e) =>
                                      setListEdits((s) => ({
                                        ...s,
                                        [listId]: e.target.value,
                                      }))
                                    }
                                    onBlur={() =>
                                      saveChecklistTitle(
                                        listId,
                                        listEdits[listId],
                                      )
                                    }
                                    onKeyDown={(e) =>
                                      handleKeyDownSave(e, () =>
                                        saveChecklistTitle(
                                          listId,
                                          listEdits[listId],
                                        ),
                                      )
                                    }
                                  />
                                ) : (
                                  <>
                                    <CustomTooltip content={displayName}>
                                      <span
                                        className="checklist-title truncate"
                                        onDoubleClick={() =>
                                          canUpdateTodoList &&
                                          startEditingList(listId, displayName)
                                        }
                                      >
                                        {displayName}
                                      </span>
                                    </CustomTooltip>

                                    <button
                                      type="button"
                                      className="info-inline-btn"
                                      onClick={() =>
                                        openDetailsModal(
                                          {
                                            ...li,
                                            toDoListId: listId,
                                            toDoListTitle: displayName,
                                            __parent: todo,
                                          },
                                          task,
                                        )
                                      }
                                      aria-label="Open checklist details"
                                    >
                                      <div>
                                        {" "}
                                        <InfoButton />
                                      </div>
                                    </button>
                                    <AddButton
                                      size={26}
                                      tooltip="Add checklist item"
                                      ariaLabel="Add checklist item"
                                      onClick={() => openChecklistInput(todoId)}
                                    />

                                    {li.remark ? (
                                      <span
                                        className="checklist-remark-text"
                                        title={li.remark}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        Remark: {li.remark}
                                      </span>
                                    ) : null}

                                    <span
                                      className="checklist-actions"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                    >
                                      <button
                                        type="button"
                                        className="checklist-actions-toggle"
                                        aria-expanded={
                                          !!checklistActionsOpen[todoId]
                                        }
                                        aria-label={`${checklistActionsOpen[todoId] ? "Hide" : "Show"} all checklist actions`}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setChecklistActionsOpen(
                                            (current) => ({
                                              ...current,
                                              [todoId]: !current[todoId],
                                            }),
                                          );
                                        }}
                                      >
                                        ⚙
                                      </button>
                                      <button
                                        type="button"
                                        className="checklist-remark-btn"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          openChecklistRemarkPrompt(li);
                                        }}
                                      >
                                        {li.remark
                                          ? "Edit Remark"
                                          : "Add Remark"}
                                      </button>
                                      {isDone ? (
                                        <span className="checklist-approved-badge">
                                          Approved
                                        </span>
                                      ) : (
                                        <button
                                          type="button"
                                          className="checklist-approve-btn"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleApproveChecklistItem(li);
                                          }}
                                        >
                                          Approve
                                        </button>
                                      )}
                                    </span>

                                    <button
                                      type="button"
                                      className="delete-inline-btn"
                                      onClick={() =>
                                        setDeleteTarget({
                                          type: "checklist",
                                          id: listId,
                                        })
                                      }
                                      aria-label="Delete checklist"
                                    >
                                      <DeleteButton size={40} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </React.Fragment>
        );
      })}

      {/* Completed tasks (collapsed by default) */}
      {/* --- Completed tasks container --- */}
      {completedTasks.length > 0 && (
        <div className="completed-section">
          <div className="section-header completed">
            <h2>Completed Stages</h2>
          </div>

          <div className="completed-list">
            {filteredCompletedTasks.map((task) => {
              const taskId = task.taskId ?? task.id;

              const roleLike = normalizeRole(currentUserRole);

              // 1️⃣ Role-based flags
              const isCreatorOrApprover =
                roleLike === "CREATOR" || roleLike === "APPROVER";

              const isViewerLike =
                roleLike === "VIEWER" || roleLike === "CLIENT";

              // 2️⃣ Ownership (task)
              const taskOwnerId =
                task?.taskOwnerId ??
                task?.ownerId ??
                task?.taskOwnerEmpId ??
                task?.createdEmpId ??
                task?.createdBy ??
                task?.assignedEmpId ??
                null;

              const isTaskOwner =
                currentUserId != null &&
                taskOwnerId != null &&
                String(taskOwnerId) === String(currentUserId);

              // 3️⃣ Permissions that DEPEND on the above
              const canViewInfo = !isViewerLike;

              const canAddItem =
                isCreatorOrApprover || (roleLike === "DOER" && isTaskOwner);

              const canChangeStage =
                isCreatorOrApprover || (roleLike === "DOER" && isTaskOwner);

              const canAddTodo =
                isCreatorOrApprover || (roleLike === "DOER" && isTaskOwner);

              const canTaskStage =
                isCreatorOrApprover || (roleLike === "DOER" && isTaskOwner);

              // Delete rights:
              const canDeleteTask =
                can(roleLike, PERM.TASK_DELETE) &&
                (roleLike === "CREATOR" ||
                  roleLike === "APPROVER" ||
                  (roleLike === "DOER" && isTaskOwner));

              const taskKey = makeExpandKey("task", taskId);

              return (
                <React.Fragment key={taskId}>
                  {/* === TASK ROW === */}
                  <div
                    className="row task-row"
                    onClick={(event) => {
                      if (
                        event.target.closest(
                          "button, input, label, a, [role='button']",
                        )
                      )
                        return;
                      event.currentTarget
                        .querySelector(".title-toggle")
                        ?.click();
                    }}
                  >
                    <div className="col title">
                      <button
                        className="toggle-btn"
                        onClick={() => {
                          // determine whether the task section will open or close
                          const opening = !expanded[taskKey];

                          // toggle the task section (existing helper)
                          toggleExpandKey(taskKey);

                          // if we're opening the task, auto-open the FIRST todo's checklist (if present)
                          if (opening) {
                            const firstTodo = (task.todoRequestDTOList ||
                              [])[0];
                            if (firstTodo) {
                              const firstTodoId =
                                firstTodo.toDoId ?? firstTodo.id;
                              if (
                                firstTodoId !== undefined &&
                                firstTodoId !== null
                              ) {
                                setTimeout(() => {
                                  setExpanded((prev) => ({
                                    ...prev,
                                    [firstTodoId]: true,
                                  }));
                                }, 10);
                              }
                            }
                          }
                        }}
                        aria-expanded={!!expanded[taskKey]}
                      >
                        {expanded[taskKey] ? "▾" : "▸"}
                      </button>

                      {/* TITLE: clickable area that behaves like the toggle button (accessible) */}
                      <div className="title-block">
                        <button
                          type="button"
                          className="title-toggle"
                          onClick={() => {
                            const opening = !expanded[taskKey];
                            toggleExpandKey(taskKey);

                            if (opening) {
                              const firstTodo = (task.todoRequestDTOList ||
                                [])[0];
                              if (firstTodo) {
                                const firstTodoId =
                                  firstTodo.toDoId ?? firstTodo.id;
                                if (
                                  firstTodoId !== undefined &&
                                  firstTodoId !== null
                                ) {
                                  setTimeout(() => {
                                    setExpanded((prev) => ({
                                      ...prev,
                                      [firstTodoId]: true,
                                    }));
                                  }, 10);
                                }
                              }
                            }
                          }}
                          aria-expanded={!!expanded[taskKey]}
                          onKeyDown={(e) => {
                            // handle Enter / Space like the button (space/enter should toggle)
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.currentTarget.click();
                            }
                          }}
                        >
                          <div className="main">
                            <CustomTooltip content={task.taskTitle || ""}>
                              <span className="title-text truncate">
                                {task.taskTitle}
                              </span>
                            </CustomTooltip>
                            <span className="able-badge">Stage</span>
                          </div>
                        </button>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      id={`mobile-more-${taskId}`}
                      className="mobile-more-checkbox"
                      tabIndex={0}
                    />
                    <label
                      htmlFor={`mobile-more-${taskId}`}
                      className="mobile-more-toggle"
                    >
                      <span className="mobile-more-toggle-show">More</span>
                      <span className="mobile-more-toggle-hide">Less</span>
                    </label>

                    <div className="col owner col--collapsible">
                      {task.taskOwnerName ? (
                        <div className="owner-badge">
                          <span className="owner-avatar">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="#1877ebff"
                            >
                              <path
                                d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 
              1.79-4 4 1.79 4 4 4zm0 2c-2.67 
              0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                              />
                            </svg>
                          </span>
                          <span className="owner-name">
                            {task.taskOwnerName}
                          </span>
                        </div>
                      ) : (
                        <span className="muted555 sm">—</span>
                      )}
                    </div>
                    <div className="col timeline col--collapsible">
                      {task.startDate && task.endDate ? (
                        (() => {
                          const { progress, isOverdue } = calcProgress(
                            task.startDate,
                            task.endDate,
                          );
                          return (
                            <TooltipPortal
                              label={
                                isOverdue
                                  ? `${getOverdueDays(
                                      task.endDate,
                                    )} days overdue`
                                  : `Start: ${fmt(task.startDate)} | End: ${fmt(
                                      task.endDate,
                                    )} • ${Math.round(progress * 100)}%`
                              }
                              offsetY={28}
                            >
                              <div
                                className={`timeline-bar5 ${
                                  isOverdue ? "overdue" : ""
                                }`}
                              >
                                {isOverdue ? (
                                  <span className="overdue-text">
                                    {getOverdueDays(task.endDate)} days overdue
                                  </span>
                                ) : (
                                  <div
                                    className="timeline-fill5"
                                    style={{ width: `${progress * 100}%` }}
                                  />
                                )}
                              </div>
                            </TooltipPortal>
                          );
                        })()
                      ) : (
                        <span className="muted555">—</span>
                      )}
                    </div>
                    <div className="col priority col--collapsible">
                      <div className="priority-cell">
                        <span
                          className={`tableview-priority-badge ${String(
                            task.priority || "medium",
                          ).toLowerCase()}`}
                        >
                          {task.priority || "Medium"}
                        </span>
                      </div>
                    </div>

                    <div className="col details col--collapsible">
                      {canViewInfo ? (
                        <span
                          className="info-btn-wrap"
                          role="button"
                          tabIndex={0}
                          onClick={() => openDetailsModal(task)}
                        >
                          <InfoButton />
                        </span>
                      ) : (
                        <span
                          className="info-btn-wrap disabled"
                          title="You don't have permission to view details"
                          style={{ opacity: 0.5, cursor: "not-allowed" }}
                        >
                          <InfoButton />
                        </span>
                      )}
                    </div>

                    <div className="col stage col--collapsible">
                      <div
                        onMouseDown={(e) => {
                          if (!canTaskStage) {
                            e.preventDefault();
                            setToast({
                              show: true,
                              type: "info",
                              message:
                                "Only the task owner, Creator, or Approver can change the task stage.",
                            });
                          }
                        }}
                        title={
                          !canTaskStage
                            ? "Only the task owner, Creator, or Approver can change the task stage"
                            : ""
                        }
                      >
                        <FoldButton
                          label="Stage"
                          options={stageOptions}
                          value={task.stagePositionId}
                          onSelect={(newStageId) => {
                            const prevStageId = task.stagePositionId;
                            setTasks((prev) =>
                              prev.map((t) =>
                                t.taskId === task.taskId
                                  ? { ...t, stagePositionId: newStageId }
                                  : t,
                              ),
                            );
                            const selected = stageOptions.find(
                              (s) => String(s.value) === String(newStageId),
                            );
                            const selectedLabel = selected?.label ?? "";
                            const flag = selected?.flag ?? "";
                            const isFinal =
                              flag &&
                              String(flag).toLowerCase().includes("final");
                            openStageRemarkPrompt({
                              kind: "task",
                              taskId,
                              newStageId,
                              selectedLabel,
                              isFinal,
                              prevStageId,
                            });
                          }}
                          disabled={!canChangeStage}
                        />
                      </div>
                      {!!(stageChangeLoading && stageChangeLoading[taskId]) && (
                        <span className="saving">Saving…</span>
                      )}
                    </div>

                    <div className="col add">
                      {canAddItem ? (
                        <AddButton
                          size={30}
                          tooltip="Quick add row"
                          ariaLabel="Quick add row"
                          onClick={addInlineRow}
                          text="Create Task"
                        />
                      ) : (
                        <button
                          className="btn btn-disabled"
                          disabled
                          title="You don't have permission to add items"
                          style={{
                            opacity: 0.9,
                            cursor: "not-allowed",
                            // height: 30,
                            // width: 30,
                          }}
                        ></button>
                      )}
                    </div>

                    <div className="col actions col--collapsible">
                      {canDeleteTask ? (
                        <DeleteButton
                          size={40}
                          onClick={() =>
                            setDeleteTarget({
                              type: "task",
                              id: taskId,
                              onConfirm: () => handleLocalDeleteTask(taskId),
                            })
                          }
                        />
                      ) : (
                        <button
                          className="btn btn-danger"
                          disabled
                          title="You don't have permission to delete tasks"
                          style={{ opacity: 0.6, cursor: "not-allowed" }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {/* === TODOs under Task === */}
                  <div
                    className={`expandable expandable-task ${
                      expanded[taskKey] ? "open" : ""
                    }`}
                  >
                    {inlineAddOpen[taskId] && expanded[taskKey] && (
                      <div
                        className="inline-add-persistent"
                        style={{
                          padding: 12,
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <div
                          className="inline-add-controls"
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                          }}
                        >
                          <InputField
                            id={`inline-add-${taskId}`}
                            label=""
                            as="input"
                            type="text"
                            name={`inlineAdd-${taskId}`}
                            placeholder="Type task title and press Enter (Esc to cancel)"
                            value={todoInputs[taskId] || ""}
                            onChange={(e) =>
                              updateTodoInput(taskId, e.target.value)
                            }
                            onKeyDown={async (e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const val = (todoInputs[taskId] || "").trim();
                                if (!val) return;
                                await createTodoInline(task, val);
                                updateTodoInput(taskId, "");
                                setTimeout(
                                  () =>
                                    document
                                      .getElementById(`inline-add-${taskId}`)
                                      ?.focus?.(),
                                  20,
                                );
                              }
                              if (e.key === "Escape") {
                                e.preventDefault();
                                updateTodoInput(taskId, "");
                                closeInlineAdd(taskId);
                              }
                            }}
                            style={{ flex: 1 }}
                          />

                          <button
                            className="btn btn-submit-inline"
                            type="button"
                            onClick={async () => {
                              const val = (todoInputs[taskId] || "").trim();
                              if (!val) return;
                              await createTodoInline(task, val);
                              updateTodoInput(taskId, "");
                              setTimeout(
                                () =>
                                  document
                                    .getElementById(`inline-add-${taskId}`)
                                    ?.focus?.(),
                                20,
                              );
                            }}
                          >
                            Submit
                          </button>

                          <button
                            className="btn btn-cancel-inline"
                            type="button"
                            onClick={() => {
                              updateTodoInput(taskId, "");
                              closeInlineAdd(taskId);
                            }}
                            title="Cancel"
                            aria-label="Cancel"
                          >
                            Cancel
                          </button>
                        </div>

                        <div
                          className="inline-add-hint"
                          style={{ marginTop: 8, color: "#777", fontSize: 13 }}
                        >
                          Add multiple tasks quickly — press Enter to add more,
                          Esc or Cancel to close.
                        </div>
                      </div>
                    )}

                    {expanded[taskKey] &&
                      (task.todoRequestDTOList || []).length === 0 && (
                        <div className="no-todo-inline" style={{ padding: 12 }}>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                            }}
                          >
                            <InputField
                              id={`first-todo-${taskId}`}
                              label=""
                              as="input"
                              type="text"
                              name={`firstTodo-${taskId}`}
                              placeholder="Type first ToDo and press Enter"
                              value={todoInputs[taskId] || ""}
                              onChange={(e) =>
                                updateTodoInput(taskId, e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const val = (todoInputs[taskId] || "").trim();
                                  if (val) createTodoInline(task, val);
                                }
                                if (e.key === "Escape") {
                                  e.preventDefault();
                                  updateTodoInput(taskId, "");
                                }
                              }}
                              style={{ flex: 1 }}
                            />
                            <button
                              className="btn btn-submit-inline"
                              type="button"
                              onClick={() => {
                                const val = (todoInputs[taskId] || "").trim();
                                if (val) createTodoInline(task, val);
                              }}
                            >
                              Submit
                            </button>
                          </div>

                          <div
                            style={{
                              marginTop: 8,
                              color: "#777",
                              fontSize: 13,
                            }}
                          >
                            No Task yet — press Enter to create the first Task
                          </div>
                        </div>
                      )}

                    {/* --- Existing todos rendering (unchanged) --- */}
                    {(task.todoRequestDTOList || []).map((todo) => {
                      const todoId = todo.toDoId ?? todo.id;
                      const allowedForThisTodo =
                        !!canUpdateTodo &&
                        (!canChangeTodoStageFor ||
                          canChangeTodoStageFor(todo, task));

                      const todoOwnerId =
                        todo?.assignedEmpId ??
                        todo?.empId ??
                        todo?.assigneeId ??
                        null;
                      const isTodoOwner =
                        currentUserId != null &&
                        todoOwnerId != null &&
                        String(todoOwnerId) === String(currentUserId);

                      const canTodoStage =
                        isCreatorOrApprover ||
                        (roleLike === "DOER" && (isTaskOwner || isTodoOwner));

                      const canDeleteTodo =
                        can(roleLike, PERM.TODO_DELETE) &&
                        (roleLike === "CREATOR" ||
                          roleLike === "APPROVER" ||
                          (roleLike === "DOER" &&
                            (isTaskOwner || isTodoOwner)));

                      const { progress: todoProgress, isOverdue: todoOverdue } =
                        calcProgress(
                          todo.startDate ?? task.startDate,
                          todo.endDate ?? task.endDate,
                        );

                      return (
                        <React.Fragment key={todoId}>
                          <div
                            className={`row todo-row ${
                              todo._isNew ? "new-item" : ""
                            } ${todo._isReady ? "ready" : ""}`}
                          >
                            <div className="col title">
                              <button
                                className="toggle-btn"
                                onClick={() => {
                                  toggleExpand(todoId);

                                  // wait for UI to expand, then focus the input field
                                  setTimeout(() => {
                                    document
                                      .getElementById(
                                        `inline-checklist-${todoId}`,
                                      )
                                      ?.focus?.();
                                  }, 20);
                                }}
                                aria-expanded={!!expanded[todoId]}
                                aria-controls={`todo-checklist-${todoId}`}
                                aria-label={`${expanded[todoId] ? "Collapse" : "Expand"} subtask ${todo.toDoTitle || ""}`}
                              >
                                {expanded[todoId] ? "▾" : "▸"}
                              </button>

                              <span className="todo-title-text">
                                <CustomTooltip
                                  content={todo.toDoTitle || "(no title)"}
                                >
                                  <button
                                    type="button"
                                    className="title-toggle todo truncate"
                                    onClick={() => {
                                      toggleExpand(todoId);
                                      setTimeout(() => {
                                        document
                                          .getElementById(
                                            `inline-checklist-${todoId}`,
                                          )
                                          ?.focus?.();
                                      }, 20);
                                    }}
                                    aria-expanded={!!expanded[todoId]}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        e.currentTarget.click();
                                      }
                                    }}
                                  >
                                    {todo.toDoTitle || "(no title)"}
                                  </button>
                                </CustomTooltip>
                              </span>
                              {todo.remark && (
                                <CustomTooltip content={todo.remark}>
                                  <span
                                    className="todo-remark-icon"
                                    style={{
                                      marginLeft: 6,
                                      fontSize: 12,
                                      color: "#6b7280",
                                      cursor: "help",
                                    }}
                                    title="Has remark"
                                  >
                                    📝
                                  </span>
                                </CustomTooltip>
                              )}
                              <span className="able-badge todo">Task</span>
                            </div>
                            <input
                              type="checkbox"
                              id={`mobile-more-todo-${todoId}`}
                              className="mobile-more-checkbox"
                              tabIndex={0}
                            />
                            <label
                              htmlFor={`mobile-more-todo-${todoId}`}
                              className="mobile-more-toggle"
                            >
                              <span className="mobile-more-toggle-show">
                                More
                              </span>
                              <span className="mobile-more-toggle-hide">
                                Less
                              </span>
                            </label>

                            {/* <div className="col progressnumber ">
                              {(() => {
                                const { done, total } =
                                  getTodoChecklistProgress(todo);
                                return total > 0 ? (
                                  <span className="checklist-badge">
                                    {done}/{total}
                                  </span>
                                ) : (
                                  "—"
                                );
                              })()}
                            </div> */}

                            <div className="col owner col--collapsible">
                              {todo.assignedEmpName ? (
                                <div className="owner-badge">
                                  <span className="owner-avatar">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="#1877ebff"
                                    >
                                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                  </span>
                                  <span className="owner-name">
                                    {todo.assignedEmpName}
                                  </span>
                                </div>
                              ) : (
                                <span className="muted555 sm">—</span>
                              )}
                            </div>

                            <div className="col timeline col--collapsible">
                              <div
                                className={`timeline-bar ${
                                  todoOverdue ? "overdue" : ""
                                }`}
                                title={`Start: ${
                                  todo.startDate ?? task.startDate ?? "?"
                                } | End: ${
                                  todo.endDate ?? task.endDate ?? "?"
                                }`}
                              >
                                <div
                                  className="timeline-fill"
                                  style={{ width: `${todoProgress * 100}%` }}
                                />
                              </div>
                            </div>

                            <div className="col priority col--collapsible">
                              <PriorityBadge priority={todo.priority} />
                            </div>

                            <div className="col details col--collapsible">
                              <span
                                className="info-btn-wrap"
                                role="button"
                                tabIndex={0}
                                onClick={() => openDetailsModal(todo, task)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    openDetailsModal(todo, task);
                                  }
                                }}
                              >
                                <InfoButton />
                              </span>
                            </div>

                            <div className="col stage col--collapsible">
                              <div
                                onMouseDown={(e) => {
                                  if (!allowedForThisTodo || !canTodoStage) {
                                    e.preventDefault();
                                    setToast({
                                      show: true,
                                      type: "info",
                                      message:
                                        "You can change stage only for your own ToDos or tasks you own.",
                                    });
                                  }
                                }}
                                title={
                                  !allowedForThisTodo || !canTodoStage
                                    ? "Stage change not allowed here"
                                    : ""
                                }
                              >
                                <FoldButton
                                  label="Stage"
                                  options={stageOptions}
                                  value={todo.stagePositionId}
                                  onSelect={(newStageId) => {
                                    if (
                                      String(newStageId) ===
                                      String(todo.stagePositionId)
                                    )
                                      return;
                                    const tid = todoId;
                                    if (todoStageInFlightRef.current.has(tid))
                                      return;
                                    todoStageInFlightRef.current.add(tid);
                                    const prevStageId = todo.stagePositionId;

                                    setTasks((prev) =>
                                      prev.map((t) =>
                                        t.taskId === task.taskId
                                          ? {
                                              ...t,
                                              todoRequestDTOList:
                                                t.todoRequestDTOList.map(
                                                  (td) =>
                                                    (td.toDoId ?? td.id) === tid
                                                      ? {
                                                          ...td,
                                                          stagePositionId:
                                                            newStageId,
                                                        }
                                                      : td,
                                                ),
                                            }
                                          : t,
                                      ),
                                    );

                                    openStageRemarkPrompt({
                                      kind: "todo",
                                      todoId: tid,
                                      taskId: task.taskId,
                                      newStageId,
                                      prevStageId,
                                    });
                                    // 🔥 notify sidebar too
                                    emitProjectUpdate(project?.projectId);
                                  }}
                                  disabled={
                                    !allowedForThisTodo || !canTodoStage
                                  }
                                />
                              </div>
                            </div>

                            <div className="col add">
                              {can(role, PERM.TODOLIST_UPDATE) && (
                                <AddButton
                                  size={28}
                                  text="Create Task"
                                  textPosition="right"
                                  // tooltip="Add ToDo to this task (click = inline add; Shift+Click = modal)"
                                  ariaLabel={`Add todo inline for task ${taskId}`}
                                  onClick={(e) => {
                                    // permission guard (keep your existing behaviour)
                                    if (!canAddTodo) {
                                      setToast({
                                        show: true,
                                        message:
                                          "Only the task owner, Creator, or Approver can add ToDos.",
                                        type: "info",
                                      });
                                      return;
                                    }

                                    // If user Shift+Clicks -> open full modal
                                    if (e.shiftKey) {
                                      setTodoModal({
                                        open: true,
                                        taskId: taskId,
                                        stagePositionId:
                                          task.stagePositionId ?? null,
                                      });
                                      return;
                                    }

                                    // Otherwise open persistent inline add box for the parent task
                                    openInlineAdd(taskId);
                                    // focus the input next tick
                                    setTimeout(
                                      () =>
                                        document
                                          .getElementById(
                                            `inline-add-${taskId}`,
                                          )
                                          ?.focus?.(),
                                      30,
                                    );
                                  }}
                                />
                              )}
                            </div>

                            <div className="col actions col--collapsible">
                              {canDeleteTodo && onDeleteTodo ? (
                                <DeleteButton
                                  size={40}
                                  onClick={() =>
                                    setDeleteTarget({
                                      type: "todo",
                                      id: todoId,
                                    })
                                  }
                                />
                              ) : (
                                <span
                                  style={{ color: "#999", fontSize: 12 }}
                                  title="You don't have permission to delete todos"
                                >
                                  —
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Expandable checklist rows */}
                          <div
                            id={`todo-checklist-${todoId}`}
                            className={`expandable expandable-todo ${
                              expanded[todoId] ? "open" : ""
                            } ${checklistActionsOpen[todoId] ? "checklist-actions-open" : ""}`}
                          >
                            {/* ---------- ALWAYS-VISIBLE inline checklist input (top of expandable-todo) ---------- */}
                            {/* ---------- INLINE CHECKLIST INPUT (show/hide input only) ---------- */}
                            {(checklistVisible[todoId] ?? false) && (
                              <div
                                style={{
                                  padding: "10px 12px",
                                  borderBottom: "1px solid #e5e7eb",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 8,
                                    alignItems: "center",
                                  }}
                                >
                                  <InputField
                                    id={`inline-checklist-${todoId}`}
                                    as="input"
                                    label=""
                                    type="text"
                                    placeholder="Add checklist item — press Enter to add (Esc to hide input)"
                                    value={checklistInputs[todoId] || ""}
                                    onChange={(e) =>
                                      updateChecklistInput(
                                        todoId,
                                        e.target.value,
                                      )
                                    }
                                    onKeyDown={async (e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        const val = (
                                          checklistInputs[todoId] || ""
                                        ).trim();
                                        if (!val) return;
                                        await createChecklistInline(
                                          todo,
                                          val,
                                          checklistRemarkInputs[todoId] || "",
                                        );
                                        updateChecklistInput(todoId, "");
                                        updateChecklistRemarkInput(todoId, "");
                                        setTimeout(
                                          () =>
                                            document
                                              .getElementById(
                                                `inline-checklist-${todoId}`,
                                              )
                                              ?.focus?.(),
                                          10,
                                        );
                                      }
                                      if (e.key === "Escape") {
                                        // Hide only the input — do NOT collapse the todo
                                        e.preventDefault();
                                        updateChecklistInput(todoId, "");
                                        updateChecklistRemarkInput(todoId, "");
                                        setChecklistVisible((s) => ({
                                          ...s,
                                          [todoId]: false,
                                        }));
                                      }
                                    }}
                                    style={{ flex: 1 }}
                                  />

                                  <InputField
                                    id={`inline-checklist-remark-${todoId}`}
                                    as="input"
                                    label=""
                                    type="text"
                                    placeholder="Remark (optional)"
                                    value={checklistRemarkInputs[todoId] || ""}
                                    onChange={(e) =>
                                      updateChecklistRemarkInput(
                                        todoId,
                                        e.target.value,
                                      )
                                    }
                                    onKeyDown={async (e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        const val = (
                                          checklistInputs[todoId] || ""
                                        ).trim();
                                        if (!val) return;
                                        await createChecklistInline(
                                          todo,
                                          val,
                                          checklistRemarkInputs[todoId] || "",
                                        );
                                        updateChecklistInput(todoId, "");
                                        updateChecklistRemarkInput(todoId, "");
                                      }
                                      if (e.key === "Escape") {
                                        e.preventDefault();
                                        updateChecklistInput(todoId, "");
                                        updateChecklistRemarkInput(todoId, "");
                                        setChecklistVisible((s) => ({
                                          ...s,
                                          [todoId]: false,
                                        }));
                                      }
                                    }}
                                    style={{ flex: "0 0 160px" }}
                                  />

                                  <button
                                    className="btn btn-submit-inline"
                                    onClick={async () => {
                                      const val = (
                                        checklistInputs[todoId] || ""
                                      ).trim();
                                      if (!val) return;
                                      await createChecklistInline(
                                        todo,
                                        val,
                                        checklistRemarkInputs[todoId] || "",
                                      );
                                      updateChecklistInput(todoId, "");
                                      updateChecklistRemarkInput(todoId, "");
                                      setTimeout(
                                        () =>
                                          document
                                            .getElementById(
                                              `inline-checklist-${todoId}`,
                                            )
                                            ?.focus?.(),
                                        10,
                                      );
                                    }}
                                  >
                                    Submit
                                  </button>

                                  <button
                                    className="btn btn-cancel-inline"
                                    type="button"
                                    onClick={() => {
                                      // Hide only the input (do NOT collapse the todo)
                                      updateChecklistInput(todoId, "");
                                      updateChecklistRemarkInput(todoId, "");
                                      setChecklistVisible((s) => ({
                                        ...s,
                                        [todoId]: false,
                                      }));
                                    }}
                                    title="Cancel"
                                    aria-label="Cancel adding checklist item"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}

                            {(() => {
                              const lists = sortChecklistsPendingFirst(
                                todo.toDoListResponseDTOList || [],
                              );
                              if (!lists.length) return null;
                              return lists.map((li) => {
                                const listId = li.toDoListId ?? li.id;
                                const isDone = Boolean(
                                  li?.completion ??
                                  li?.completionFlag ??
                                  (li?.status &&
                                    String(li.status).toUpperCase() === "DONE"),
                                );
                                const displayName =
                                  li.toDoListTitle?.trim?.() || "Checklist";

                                const setDone = (nextValue) => {
                                  if (!canUpdateTodoList) return;
                                  setTasks((prev) =>
                                    (prev || []).map((t) => {
                                      if (
                                        Number(t.taskId ?? t.id) !==
                                        Number(task.taskId ?? task.id)
                                      )
                                        return t;
                                      return {
                                        ...t,
                                        todoRequestDTOList: (
                                          t.todoRequestDTOList || []
                                        ).map((td) => {
                                          if (
                                            Number(td.toDoId ?? td.id) !==
                                            Number(todoId)
                                          )
                                            return td;
                                          return {
                                            ...td,
                                            toDoListResponseDTOList: (
                                              td.toDoListResponseDTOList || []
                                            ).map((item) => {
                                              const iKey = Number(
                                                item.toDoListId ?? item.id,
                                              );
                                              if (iKey !== Number(listId))
                                                return item;
                                              return {
                                                ...item,
                                                completion: !!nextValue,
                                                status: nextValue
                                                  ? "DONE"
                                                  : "NOT_DONE",
                                              };
                                            }),
                                          };
                                        }),
                                      };
                                    }),
                                  );

                                  if (typeof onToggleChecklist === "function") {
                                    onToggleChecklist(
                                      listId,
                                      !!nextValue,
                                    ).catch((e) =>
                                      console.error(
                                        "toggle checklist failed",
                                        e,
                                      ),
                                    );
                                  } else if (
                                    typeof onUpdateTodoList === "function"
                                  ) {
                                    onUpdateTodoList(listId, {
                                      completion: !!nextValue,
                                    }).catch((e) =>
                                      console.error(
                                        "toggle checklist (fallback) failed",
                                        e,
                                      ),
                                    );
                                  }
                                };

                                return (
                                  <div
                                    className={`checklist-line ${
                                      li._isNew ? "new-item" : ""
                                    } ${li._isReady ? "ready" : ""}`}
                                    key={listId}
                                  >
                                    <div className="checklist-checkbox">
                                      <CheckList
                                        id={`checklist-${listId}`}
                                        label=""
                                        checked={isDone}
                                        disabled={!canUpdateTodoList}
                                        onChange={(e) =>
                                          setDone(!!e?.target?.checked)
                                        }
                                      />
                                    </div>

                                    <div className="checklist-main">
                                      {listEdits[listId] !== undefined ? (
                                        <input
                                          autoFocus
                                          value={listEdits[listId]}
                                          onChange={(e) =>
                                            setListEdits((s) => ({
                                              ...s,
                                              [listId]: e.target.value,
                                            }))
                                          }
                                          onBlur={() =>
                                            saveChecklistTitle(
                                              listId,
                                              listEdits[listId],
                                            )
                                          }
                                          onKeyDown={(e) =>
                                            handleKeyDownSave(e, () =>
                                              saveChecklistTitle(
                                                listId,
                                                listEdits[listId],
                                              ),
                                            )
                                          }
                                        />
                                      ) : (
                                        <>
                                          <CustomTooltip content={displayName}>
                                            <span
                                              className="checklist-title truncate"
                                              onDoubleClick={() =>
                                                canUpdateTodoList &&
                                                startEditingList(
                                                  listId,
                                                  displayName,
                                                )
                                              }
                                            >
                                              {displayName}
                                            </span>
                                          </CustomTooltip>

                                          <button
                                            type="button"
                                            className="info-inline-btn"
                                            onClick={() =>
                                              openDetailsModal(
                                                {
                                                  ...li,
                                                  toDoListId: listId,
                                                  toDoListTitle: displayName,
                                                  __parent: todo,
                                                },
                                                task,
                                              )
                                            }
                                            aria-label="Open checklist details"
                                          >
                                            <div>
                                              {" "}
                                              <InfoButton />
                                            </div>
                                          </button>

                                          <button
                                            type="button"
                                            className="delete-inline-btn"
                                            onClick={() =>
                                              setDeleteTarget({
                                                type: "checklist",
                                                id: listId,
                                              })
                                            }
                                            aria-label="Delete checklist"
                                          >
                                            <DeleteButton size={40} />
                                          </button>
                                          <AddButton
                                            size={28}
                                            text={null}
                                            tooltip="Add checklist item"
                                            ariaLabel={`Show add checklist input for todo ${todoId}`}
                                            onClick={() => {
                                              // show the inline checklist input for the parent todo
                                              setChecklistVisible((s) => ({
                                                ...s,
                                                [todoId]: true,
                                              }));
                                              // focus the input quickly so user can type
                                              setTimeout(() => {
                                                document
                                                  .getElementById(
                                                    `inline-checklist-${todoId}`,
                                                  )
                                                  ?.focus?.();
                                              }, 20);
                                            }}
                                          />
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
      {/* --- Upcoming tasks container --- */}
      {upcomingTasks.length > 0 && (
        <div className="upcoming-section">
          <div className="section-header upcoming">
            <h2>Upcoming stages</h2>
            {/* optional small label: */}
            {/* <span className="muted">Starts later</span> */}
          </div>

          <div className="upcoming-list">
            {filteredUpcomingTasks.map((task) => {
              const taskId = task.taskId ?? task.id;

              const roleLike = normalizeRole(currentUserRole);

              // 1️⃣ Role-based flags
              const isCreatorOrApprover =
                roleLike === "CREATOR" || roleLike === "APPROVER";

              const isViewerLike =
                roleLike === "VIEWER" || roleLike === "CLIENT";

              // 2️⃣ Ownership (task)
              const taskOwnerId =
                task?.taskOwnerId ??
                task?.ownerId ??
                task?.taskOwnerEmpId ??
                task?.createdEmpId ??
                task?.createdBy ??
                task?.assignedEmpId ??
                null;

              const isTaskOwner =
                currentUserId != null &&
                taskOwnerId != null &&
                String(taskOwnerId) === String(currentUserId);

              // 3️⃣ Permissions that DEPEND on the above
              const canViewInfo = !isViewerLike;

              const canAddItem =
                isCreatorOrApprover || (roleLike === "DOER" && isTaskOwner);

              const canChangeStage =
                isCreatorOrApprover || (roleLike === "DOER" && isTaskOwner);

              const canAddTodo =
                isCreatorOrApprover || (roleLike === "DOER" && isTaskOwner);

              const canTaskStage =
                isCreatorOrApprover || (roleLike === "DOER" && isTaskOwner);

              // Delete rights:
              const canDeleteTask =
                can(roleLike, PERM.TASK_DELETE) &&
                (roleLike === "CREATOR" ||
                  roleLike === "APPROVER" ||
                  (roleLike === "DOER" && isTaskOwner));

              const taskKey = makeExpandKey("task", taskId);

              return (
                <React.Fragment key={taskId}>
                  {/* === TASK ROW === */}
                  <div
                    className="row task-row"
                    onClick={(event) => {
                      if (
                        event.target.closest(
                          "button, input, label, a, [role='button']",
                        )
                      )
                        return;
                      event.currentTarget
                        .querySelector(".title-toggle")
                        ?.click();
                    }}
                  >
                    <div className="col title">
                      <button
                        className="toggle-btn"
                        onClick={() => {
                          // determine whether the task section will open or close
                          const opening = !expanded[taskKey];

                          // toggle the task section (existing helper)
                          toggleExpandKey(taskKey);

                          // if we're opening the task, auto-open the FIRST todo's checklist (if present)
                          if (opening) {
                            const firstTodo = (task.todoRequestDTOList ||
                              [])[0];
                            if (firstTodo) {
                              const firstTodoId =
                                firstTodo.toDoId ?? firstTodo.id;
                              if (
                                firstTodoId !== undefined &&
                                firstTodoId !== null
                              ) {
                                setTimeout(() => {
                                  setExpanded((prev) => ({
                                    ...prev,
                                    [firstTodoId]: true,
                                  }));
                                }, 10);
                              }
                            }
                          }
                        }}
                        aria-expanded={!!expanded[taskKey]}
                      >
                        {expanded[taskKey] ? "▾" : "▸"}
                      </button>

                      {/* TITLE: clickable area that behaves like the toggle button (accessible) */}
                      <div className="title-block">
                        <button
                          type="button"
                          className="title-toggle"
                          onClick={() => {
                            // same toggle behavior as the left toggle button
                            const opening = !expanded[taskKey];
                            toggleExpandKey(taskKey);

                            if (opening) {
                              const firstTodo = (task.todoRequestDTOList ||
                                [])[0];
                              if (firstTodo) {
                                const firstTodoId =
                                  firstTodo.toDoId ?? firstTodo.id;
                                if (
                                  firstTodoId !== undefined &&
                                  firstTodoId !== null
                                ) {
                                  setTimeout(() => {
                                    setExpanded((prev) => ({
                                      ...prev,
                                      [firstTodoId]: true,
                                    }));
                                  }, 10);
                                }
                              }
                            }
                          }}
                          aria-expanded={!!expanded[taskKey]}
                          onKeyDown={(e) => {
                            // handle Enter / Space like the button (space/enter should toggle)
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.currentTarget.click();
                            }
                          }}
                        >
                          <div className="main">
                            <CustomTooltip content={task.taskTitle || ""}>
                              <span className="title-text truncate">
                                {task.taskTitle}
                              </span>
                            </CustomTooltip>
                            <span className="able-badge">Stage</span>
                          </div>
                        </button>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      id={`mobile-more-${taskId}`}
                      className="mobile-more-checkbox"
                      tabIndex={0}
                    />
                    <label
                      htmlFor={`mobile-more-${taskId}`}
                      className="mobile-more-toggle"
                    >
                      <span className="mobile-more-toggle-show">More</span>
                      <span className="mobile-more-toggle-hide">Less</span>
                    </label>

                    {/* Progress column (todos completed) */}
                    {/* <div className="col progressnumber ">
                      {(() => {
                        const { done, total } = getTaskTodoProgress(task);
                        return total > 0 ? (
                          <span className="checklist-badge">
                            {done}/{total}
                          </span>
                        ) : (
                          "--"
                        );
                      })()}
                    </div> */}

                    <div className="col owner col--collapsible">
                      {task.taskOwnerName ? (
                        <div className="owner-badge">
                          <span className="owner-avatar">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="#1877ebff"
                            >
                              <path
                                d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 
              1.79-4 4 1.79 4 4 4zm0 2c-2.67 
              0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                              />
                            </svg>
                          </span>
                          <span className="owner-name">
                            {task.taskOwnerName}
                          </span>
                        </div>
                      ) : (
                        <span className="muted555 sm">—</span>
                      )}
                    </div>
                    <div className="col timeline col--collapsible">
                      {task.startDate && task.endDate ? (
                        (() => {
                          const { progress, isOverdue } = calcProgress(
                            task.startDate,
                            task.endDate,
                          );
                          return (
                            <TooltipPortal
                              label={
                                isOverdue
                                  ? `${getOverdueDays(
                                      task.endDate,
                                    )} days overdue`
                                  : `Start: ${fmt(task.startDate)} | End: ${fmt(
                                      task.endDate,
                                    )} • ${Math.round(progress * 100)}%`
                              }
                              offsetY={28}
                            >
                              <div
                                className={`timeline-bar5 ${
                                  isOverdue ? "overdue" : ""
                                }`}
                              >
                                {isOverdue ? (
                                  <span className="overdue-text">
                                    {getOverdueDays(task.endDate)} days overdue
                                  </span>
                                ) : (
                                  <div
                                    className="timeline-fill5"
                                    style={{
                                      width: `${progress * 100}%`,
                                    }}
                                  />
                                )}
                              </div>
                            </TooltipPortal>
                          );
                        })()
                      ) : (
                        <span className="muted555">—</span>
                      )}
                    </div>
                    <div className="col priority col--collapsible">
                      <div className="priority-cell">
                        <span
                          className={`tableview-priority-badge ${String(
                            task.priority || "medium",
                          ).toLowerCase()}`}
                        >
                          {task.priority || "Medium"}
                        </span>
                      </div>
                    </div>

                    <div className="col details col--collapsible">
                      {canViewInfo ? (
                        <span
                          className="info-btn-wrap"
                          role="button"
                          tabIndex={0}
                          onClick={() => openDetailsModal(task)}
                        >
                          <InfoButton />
                        </span>
                      ) : (
                        <span
                          className="info-btn-wrap disabled"
                          title="You don't have permission to view details"
                          style={{ opacity: 0.5, cursor: "not-allowed" }}
                        >
                          <InfoButton />
                        </span>
                      )}
                    </div>

                    <div className="col stage col--collapsible">
                      <div
                        onMouseDown={(e) => {
                          if (!canTaskStage) {
                            e.preventDefault();
                            setToast({
                              show: true,
                              type: "info",
                              message:
                                "Only the task owner, Creator, or Approver can change the task stage.",
                            });
                          }
                        }}
                        title={
                          !canTaskStage
                            ? "Only the task owner, Creator, or Approver can change the task stage"
                            : ""
                        }
                      >
                        <FoldButton
                          label="Stage"
                          options={stageOptions}
                          value={task.stagePositionId}
                          onSelect={(newStageId) => {
                            const prevStageId = task.stagePositionId;
                            setTasks((prev) =>
                              prev.map((t) =>
                                t.taskId === task.taskId
                                  ? { ...t, stagePositionId: newStageId }
                                  : t,
                              ),
                            );
                            const selected = stageOptions.find(
                              (s) => String(s.value) === String(newStageId),
                            );
                            const selectedLabel = selected?.label ?? "";
                            const flag = selected?.flag ?? "";
                            const isFinal =
                              flag &&
                              String(flag).toLowerCase().includes("final");
                            openStageRemarkPrompt({
                              kind: "task",
                              taskId,
                              newStageId,
                              selectedLabel,
                              isFinal,
                              prevStageId,
                            });
                          }}
                          disabled={!canChangeStage}
                        />
                      </div>
                      {!!(stageChangeLoading && stageChangeLoading[taskId]) && (
                        <span className="saving">Saving…</span>
                      )}
                    </div>
                    <div className="col add">
                      {canAddItem ? (
                        <AddButton
                          size={30}
                          tooltip="Quick add row"
                          ariaLabel="Quick add row"
                          onClick={addInlineRow}
                          text="Create Task"
                        />
                      ) : (
                        <button
                          className="btn btn-disabled"
                          disabled
                          title="You don't have permission to add items"
                          style={{
                            opacity: 0.9,
                            cursor: "not-allowed",
                            // height: 30,
                            // width: 30,
                          }}
                        ></button>
                      )}
                    </div>

                    <div className="col actions col--collapsible">
                      {canDeleteTask ? (
                        <DeleteButton
                          size={40}
                          onClick={() =>
                            setDeleteTarget({
                              type: "task",
                              id: taskId,
                              onConfirm: () => handleLocalDeleteTask(taskId),
                            })
                          }
                        />
                      ) : (
                        <button
                          className="btn btn-danger"
                          disabled
                          title="You don't have permission to delete tasks"
                          style={{ opacity: 0.6, cursor: "not-allowed" }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {/* === TODOs under Task === */}
                  <div
                    className={`expandable expandable-task ${
                      expanded[taskKey] ? "open" : ""
                    }`}
                  >
                    {/* Inline input shown only when expanded and there are no todos */}
                    {/* Persistent inline add for this task (appears when Add button clicked inside a ToDo row) */}
                    {/* Persistent inline add for this task — always stays visible once opened */}
                    {inlineAddOpen[taskId] && expanded[taskKey] && (
                      <div
                        className="inline-add-persistent"
                        style={{
                          padding: 12,
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                          }}
                        >
                          <InputField
                            id={`inline-add-${taskId}`}
                            label=""
                            as="input"
                            type="text"
                            name={`inlineAdd-${taskId}`}
                            placeholder="Type task title and press Enter (Esc to cancel)"
                            value={todoInputs[taskId] || ""}
                            onChange={(e) =>
                              updateTodoInput(taskId, e.target.value)
                            }
                            onKeyDown={async (e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const val = (todoInputs[taskId] || "").trim();
                                if (!val) return;
                                await createTodoInline(task, val);
                                updateTodoInput(taskId, "");
                                setTimeout(
                                  () =>
                                    document
                                      .getElementById(`inline-add-${taskId}`)
                                      ?.focus?.(),
                                  20,
                                );
                              }
                              if (e.key === "Escape") {
                                e.preventDefault();
                                updateTodoInput(taskId, "");
                                closeInlineAdd(taskId);
                              }
                            }}
                            style={{ flex: 1 }}
                          />

                          <button
                            className="btn btn-submit-inline"
                            type="button"
                            onClick={async () => {
                              const val = (todoInputs[taskId] || "").trim();
                              if (!val) return;
                              await createTodoInline(task, val);
                              updateTodoInput(taskId, "");
                              setTimeout(
                                () =>
                                  document
                                    .getElementById(`inline-add-${taskId}`)
                                    ?.focus?.(),
                                20,
                              );
                            }}
                          >
                            Submit
                          </button>

                          <button
                            className="btn btn-cancel-inline"
                            type="button"
                            onClick={() => {
                              updateTodoInput(taskId, "");
                              closeInlineAdd(taskId);
                            }}
                            title="Cancel"
                            aria-label="Cancel"
                          >
                            Cancel
                          </button>
                        </div>

                        <div
                          style={{
                            marginTop: 8,
                            color: "#777",
                            fontSize: 13,
                          }}
                        >
                          Add multiple tasks quickly — press Enter to add more,
                          Esc or Cancel to close.
                        </div>
                      </div>
                    )}

                    {expanded[taskKey] &&
                      (task.todoRequestDTOList || []).length === 0 && (
                        <div className="no-todo-inline" style={{ padding: 12 }}>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                            }}
                          >
                            <InputField
                              id={`first-todo-${taskId}`}
                              label=""
                              as="input"
                              type="text"
                              name={`firstTodo-${taskId}`}
                              placeholder="Type first ToDo and press Enter"
                              value={todoInputs[taskId] || ""}
                              onChange={(e) =>
                                updateTodoInput(taskId, e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const val = (todoInputs[taskId] || "").trim();
                                  if (val) createTodoInline(task, val);
                                }
                                if (e.key === "Escape") {
                                  e.preventDefault();
                                  updateTodoInput(taskId, "");
                                }
                              }}
                              style={{ flex: 1 }}
                            />
                            <button
                              className="btn btn-submit-inline"
                              type="button"
                              onClick={() => {
                                const val = (todoInputs[taskId] || "").trim();
                                if (val) createTodoInline(task, val);
                              }}
                            >
                              Submit
                            </button>
                          </div>

                          <div
                            style={{
                              marginTop: 8,
                              color: "#777",
                              fontSize: 13,
                            }}
                          >
                            No Tasks yet — press Enter to create the first Task.
                          </div>
                        </div>
                      )}

                    {/* --- Existing todos rendering (unchanged) --- */}
                    {(task.todoRequestDTOList || []).map((todo) => {
                      const todoId = todo.toDoId ?? todo.id;
                      const allowedForThisTodo =
                        !!canUpdateTodo &&
                        (!canChangeTodoStageFor ||
                          canChangeTodoStageFor(todo, task));

                      const todoOwnerId =
                        todo?.assignedEmpId ??
                        todo?.empId ??
                        todo?.assigneeId ??
                        null;
                      const isTodoOwner =
                        currentUserId != null &&
                        todoOwnerId != null &&
                        String(todoOwnerId) === String(currentUserId);

                      const canTodoStage =
                        isCreatorOrApprover ||
                        (roleLike === "DOER" && (isTaskOwner || isTodoOwner));

                      const canDeleteTodo =
                        can(roleLike, PERM.TODO_DELETE) &&
                        (roleLike === "CREATOR" ||
                          roleLike === "APPROVER" ||
                          (roleLike === "DOER" &&
                            (isTaskOwner || isTodoOwner)));

                      const { progress: todoProgress, isOverdue: todoOverdue } =
                        calcProgress(
                          todo.startDate ?? task.startDate,
                          todo.endDate ?? task.endDate,
                        );

                      return (
                        <React.Fragment key={todoId}>
                          <div
                            className={`row todo-row ${
                              todo._isNew ? "new-item" : ""
                            } ${todo._isReady ? "ready" : ""}`}
                          >
                            <div className="col title">
                              <button
                                className="toggle-btn"
                                onClick={() => {
                                  toggleExpand(todoId);

                                  // wait for UI to expand, then focus the input field
                                  setTimeout(() => {
                                    document
                                      .getElementById(
                                        `inline-checklist-${todoId}`,
                                      )
                                      ?.focus?.();
                                  }, 20);
                                }}
                                aria-expanded={!!expanded[todoId]}
                                aria-controls={`todo-checklist-${todoId}`}
                                aria-label={`${expanded[todoId] ? "Collapse" : "Expand"} subtask ${todo.toDoTitle || ""}`}
                              >
                                {expanded[todoId] ? "▾" : "▸"}
                              </button>

                              <span className="todo-title-text">
                                <CustomTooltip
                                  content={todo.toDoTitle || "(no title)"}
                                >
                                  <button
                                    type="button"
                                    className="title-toggle todo truncate"
                                    onClick={() => {
                                      toggleExpand(todoId);
                                      setTimeout(() => {
                                        document
                                          .getElementById(
                                            `inline-checklist-${todoId}`,
                                          )
                                          ?.focus?.();
                                      }, 20);
                                    }}
                                    aria-expanded={!!expanded[todoId]}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        e.currentTarget.click();
                                      }
                                    }}
                                  >
                                    {todo.toDoTitle || "(no title)"}
                                  </button>
                                </CustomTooltip>
                              </span>
                              {todo.remark && (
                                <CustomTooltip content={todo.remark}>
                                  <span
                                    className="todo-remark-icon"
                                    style={{
                                      marginLeft: 6,
                                      fontSize: 12,
                                      color: "#6b7280",
                                      cursor: "help",
                                    }}
                                    title="Has remark"
                                  >
                                    📝
                                  </span>
                                </CustomTooltip>
                              )}
                              <span className="able-badge todo">Task</span>
                            </div>
                            <input
                              type="checkbox"
                              id={`mobile-more-todo-${todoId}`}
                              className="mobile-more-checkbox"
                              tabIndex={0}
                            />
                            <label
                              htmlFor={`mobile-more-todo-${todoId}`}
                              className="mobile-more-toggle"
                            >
                              <span className="mobile-more-toggle-show">
                                More
                              </span>
                              <span className="mobile-more-toggle-hide">
                                Less
                              </span>
                            </label>

                            {/* <div className="col progressnumber ">
                              {(() => {
                                const { done, total } =
                                  getTodoChecklistProgress(todo);
                                return total > 0 ? (
                                  <span className="checklist-badge">
                                    {done}/{total}
                                  </span>
                                ) : (
                                  "—"
                                );
                              })()}
                            </div> */}

                            <div className="col owner col--collapsible">
                              {todo.assignedEmpName ? (
                                <div className="owner-badge">
                                  <span className="owner-avatar">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="#1877ebff"
                                    >
                                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                  </span>
                                  <span className="owner-name">
                                    {todo.assignedEmpName}
                                  </span>
                                </div>
                              ) : (
                                <span className="muted555 sm">—</span>
                              )}
                            </div>

                            <div className="col timeline col--collapsible">
                              <div
                                className={`timeline-bar ${
                                  todoOverdue ? "overdue" : ""
                                }`}
                                title={`Start: ${
                                  todo.startDate ?? task.startDate ?? "?"
                                } | End: ${
                                  todo.endDate ?? task.endDate ?? "?"
                                }`}
                              >
                                <div
                                  className="timeline-fill"
                                  style={{
                                    width: `${todoProgress * 100}%`,
                                  }}
                                />
                              </div>
                            </div>

                            <div className="col priority col--collapsible">
                              <PriorityBadge priority={todo.priority} />
                            </div>

                            <div className="col details col--collapsible">
                              <span
                                className="info-btn-wrap"
                                role="button"
                                tabIndex={0}
                                onClick={() => openDetailsModal(todo, task)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    openDetailsModal(todo, task);
                                  }
                                }}
                              >
                                <InfoButton />
                              </span>
                            </div>

                            <div className="col stage col--collapsible">
                              <div
                                onMouseDown={(e) => {
                                  if (!allowedForThisTodo || !canTodoStage) {
                                    e.preventDefault();
                                    setToast({
                                      show: true,
                                      type: "info",
                                      message:
                                        "You can change stage only for your own ToDos or tasks you own.",
                                    });
                                  }
                                }}
                                title={
                                  !allowedForThisTodo || !canTodoStage
                                    ? "Stage change not allowed here"
                                    : ""
                                }
                              >
                                <FoldButton
                                  label="Stage"
                                  options={stageOptions}
                                  value={todo.stagePositionId}
                                  onSelect={(newStageId) => {
                                    if (
                                      String(newStageId) ===
                                      String(todo.stagePositionId)
                                    )
                                      return;
                                    const tid = todoId;
                                    if (todoStageInFlightRef.current.has(tid))
                                      return;
                                    todoStageInFlightRef.current.add(tid);
                                    const prevStageId = todo.stagePositionId;

                                    setTasks((prev) =>
                                      prev.map((t) =>
                                        t.taskId === task.taskId
                                          ? {
                                              ...t,
                                              todoRequestDTOList:
                                                t.todoRequestDTOList.map(
                                                  (td) =>
                                                    (td.toDoId ?? td.id) === tid
                                                      ? {
                                                          ...td,
                                                          stagePositionId:
                                                            newStageId,
                                                        }
                                                      : td,
                                                ),
                                            }
                                          : t,
                                      ),
                                    );

                                    openStageRemarkPrompt({
                                      kind: "todo",
                                      todoId: tid,
                                      taskId: task.taskId,
                                      newStageId,
                                      prevStageId,
                                    });
                                    // 🔥 notify sidebar too
                                    emitProjectUpdate(project?.projectId);
                                  }}
                                  disabled={
                                    !allowedForThisTodo || !canTodoStage
                                  }
                                />
                              </div>
                            </div>

                            <div className="col add">
                              {can(role, PERM.TODOLIST_UPDATE) && (
                                <AddButton
                                  size={28}
                                  text="Create Task"
                                  textPosition="right"
                                  // tooltip="Add ToDo to this task (click = inline add; Shift+Click = modal)"
                                  ariaLabel={`Add todo inline for task ${taskId}`}
                                  onClick={(e) => {
                                    // permission guard (keep your existing behaviour)
                                    if (!canAddTodo) {
                                      setToast({
                                        show: true,
                                        message:
                                          "Only the task owner, Creator, or Approver can add ToDos.",
                                        type: "info",
                                      });
                                      return;
                                    }

                                    // If user Shift+Clicks -> open full modal
                                    if (e.shiftKey) {
                                      setTodoModal({
                                        open: true,
                                        taskId: taskId,
                                        stagePositionId:
                                          task.stagePositionId ?? null,
                                      });
                                      return;
                                    }

                                    // Otherwise open persistent inline add box for the parent task
                                    openInlineAdd(taskId);
                                    // focus the input next tick
                                    setTimeout(
                                      () =>
                                        document
                                          .getElementById(
                                            `inline-add-${taskId}`,
                                          )
                                          ?.focus?.(),
                                      30,
                                    );
                                  }}
                                />
                              )}
                            </div>

                            <div className="col actions col--collapsible">
                              {canDeleteTodo && onDeleteTodo ? (
                                <DeleteButton
                                  size={40}
                                  onClick={() =>
                                    setDeleteTarget({
                                      type: "todo",
                                      id: todoId,
                                    })
                                  }
                                />
                              ) : (
                                <span
                                  style={{ color: "#999", fontSize: 12 }}
                                  title="You don't have permission to delete todos"
                                >
                                  —
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Expandable checklist rows */}
                          <div
                            id={`todo-checklist-${todoId}`}
                            className={`expandable expandable-todo ${
                              expanded[todoId] ? "open" : ""
                            } ${checklistActionsOpen[todoId] ? "checklist-actions-open" : ""}`}
                          >
                            {/* ---------- ALWAYS-VISIBLE inline checklist input (top of expandable-todo) ---------- */}
                            {/* ---------- INLINE CHECKLIST INPUT (show/hide input only) ---------- */}
                            {(checklistVisible[todoId] ?? false) && (
                              <div
                                style={{
                                  padding: "10px 12px",
                                  borderBottom: "1px solid #e5e7eb",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 8,
                                    alignItems: "center",
                                  }}
                                >
                                  <InputField
                                    id={`inline-checklist-${todoId}`}
                                    as="input"
                                    label=""
                                    type="text"
                                    placeholder="Add checklist item — press Enter to add (Esc to hide input)"
                                    value={checklistInputs[todoId] || ""}
                                    onChange={(e) =>
                                      updateChecklistInput(
                                        todoId,
                                        e.target.value,
                                      )
                                    }
                                    onKeyDown={async (e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        const val = (
                                          checklistInputs[todoId] || ""
                                        ).trim();
                                        if (!val) return;
                                        await createChecklistInline(
                                          todo,
                                          val,
                                          checklistRemarkInputs[todoId] || "",
                                        );
                                        updateChecklistInput(todoId, "");
                                        updateChecklistRemarkInput(todoId, "");
                                        setTimeout(
                                          () =>
                                            document
                                              .getElementById(
                                                `inline-checklist-${todoId}`,
                                              )
                                              ?.focus?.(),
                                          10,
                                        );
                                      }
                                      if (e.key === "Escape") {
                                        // Hide only the input — do NOT collapse the todo
                                        e.preventDefault();
                                        updateChecklistInput(todoId, "");
                                        updateChecklistRemarkInput(todoId, "");
                                        setChecklistVisible((s) => ({
                                          ...s,
                                          [todoId]: false,
                                        }));
                                      }
                                    }}
                                    style={{ flex: 1 }}
                                  />

                                  <InputField
                                    id={`inline-checklist-remark-${todoId}`}
                                    as="input"
                                    label=""
                                    type="text"
                                    placeholder="Remark (optional)"
                                    value={checklistRemarkInputs[todoId] || ""}
                                    onChange={(e) =>
                                      updateChecklistRemarkInput(
                                        todoId,
                                        e.target.value,
                                      )
                                    }
                                    onKeyDown={async (e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        const val = (
                                          checklistInputs[todoId] || ""
                                        ).trim();
                                        if (!val) return;
                                        await createChecklistInline(
                                          todo,
                                          val,
                                          checklistRemarkInputs[todoId] || "",
                                        );
                                        updateChecklistInput(todoId, "");
                                        updateChecklistRemarkInput(todoId, "");
                                      }
                                      if (e.key === "Escape") {
                                        e.preventDefault();
                                        updateChecklistInput(todoId, "");
                                        updateChecklistRemarkInput(todoId, "");
                                        setChecklistVisible((s) => ({
                                          ...s,
                                          [todoId]: false,
                                        }));
                                      }
                                    }}
                                    style={{ flex: "0 0 160px" }}
                                  />

                                  <button
                                    className="btn btn-submit-inline"
                                    onClick={async () => {
                                      const val = (
                                        checklistInputs[todoId] || ""
                                      ).trim();
                                      if (!val) return;
                                      await createChecklistInline(
                                        todo,
                                        val,
                                        checklistRemarkInputs[todoId] || "",
                                      );
                                      updateChecklistInput(todoId, "");
                                      updateChecklistRemarkInput(todoId, "");
                                      setTimeout(
                                        () =>
                                          document
                                            .getElementById(
                                              `inline-checklist-${todoId}`,
                                            )
                                            ?.focus?.(),
                                        10,
                                      );
                                    }}
                                  >
                                    Submit
                                  </button>

                                  <button
                                    className="btn btn-cancel-inline"
                                    type="button"
                                    onClick={() => {
                                      // Hide only the input (do NOT collapse the todo)
                                      updateChecklistInput(todoId, "");
                                      updateChecklistRemarkInput(todoId, "");
                                      setChecklistVisible((s) => ({
                                        ...s,
                                        [todoId]: false,
                                      }));
                                    }}
                                    title="Cancel"
                                    aria-label="Cancel adding checklist item"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}

                            {(() => {
                              const lists = sortChecklistsPendingFirst(
                                todo.toDoListResponseDTOList || [],
                              );
                              if (!lists.length) return null;
                              return lists.map((li) => {
                                const listId = li.toDoListId ?? li.id;
                                const isDone = Boolean(
                                  li?.completion ??
                                  li?.completionFlag ??
                                  (li?.status &&
                                    String(li.status).toUpperCase() === "DONE"),
                                );
                                const displayName =
                                  li.toDoListTitle?.trim?.() || "Checklist";

                                const setDone = (nextValue) => {
                                  if (!canUpdateTodoList) return;
                                  setTasks((prev) =>
                                    (prev || []).map((t) => {
                                      if (
                                        Number(t.taskId ?? t.id) !==
                                        Number(task.taskId ?? task.id)
                                      )
                                        return t;
                                      return {
                                        ...t,
                                        todoRequestDTOList: (
                                          t.todoRequestDTOList || []
                                        ).map((td) => {
                                          if (
                                            Number(td.toDoId ?? td.id) !==
                                            Number(todoId)
                                          )
                                            return td;
                                          return {
                                            ...td,
                                            toDoListResponseDTOList: (
                                              td.toDoListResponseDTOList || []
                                            ).map((item) => {
                                              const iKey = Number(
                                                item.toDoListId ?? item.id,
                                              );
                                              if (iKey !== Number(listId))
                                                return item;
                                              return {
                                                ...item,
                                                completion: !!nextValue,
                                                status: nextValue
                                                  ? "DONE"
                                                  : "NOT_DONE",
                                              };
                                            }),
                                          };
                                        }),
                                      };
                                    }),
                                  );

                                  if (typeof onToggleChecklist === "function") {
                                    onToggleChecklist(
                                      listId,
                                      !!nextValue,
                                    ).catch((e) =>
                                      console.error(
                                        "toggle checklist failed",
                                        e,
                                      ),
                                    );
                                  } else if (
                                    typeof onUpdateTodoList === "function"
                                  ) {
                                    onUpdateTodoList(listId, {
                                      completion: !!nextValue,
                                    }).catch((e) =>
                                      console.error(
                                        "toggle checklist (fallback) failed",
                                        e,
                                      ),
                                    );
                                  }
                                };

                                return (
                                  <div
                                    className={`checklist-line ${
                                      li._isNew ? "new-item" : ""
                                    } ${li._isReady ? "ready" : ""}`}
                                    key={listId}
                                  >
                                    <div className="checklist-checkbox">
                                      <CheckList
                                        id={`checklist-${listId}`}
                                        label=""
                                        checked={isDone}
                                        disabled={!canUpdateTodoList}
                                        onChange={(e) =>
                                          setDone(!!e?.target?.checked)
                                        }
                                      />
                                    </div>

                                    <div className="checklist-main">
                                      {listEdits[listId] !== undefined ? (
                                        <input
                                          autoFocus
                                          value={listEdits[listId]}
                                          onChange={(e) =>
                                            setListEdits((s) => ({
                                              ...s,
                                              [listId]: e.target.value,
                                            }))
                                          }
                                          onBlur={() =>
                                            saveChecklistTitle(
                                              listId,
                                              listEdits[listId],
                                            )
                                          }
                                          onKeyDown={(e) =>
                                            handleKeyDownSave(e, () =>
                                              saveChecklistTitle(
                                                listId,
                                                listEdits[listId],
                                              ),
                                            )
                                          }
                                        />
                                      ) : (
                                        <>
                                          <CustomTooltip content={displayName}>
                                            <span
                                              className="checklist-title truncate"
                                              onDoubleClick={() =>
                                                canUpdateTodoList &&
                                                startEditingList(
                                                  listId,
                                                  displayName,
                                                )
                                              }
                                            >
                                              {displayName}
                                            </span>
                                          </CustomTooltip>

                                          <button
                                            type="button"
                                            className="info-inline-btn"
                                            onClick={() =>
                                              openDetailsModal(
                                                {
                                                  ...li,
                                                  toDoListId: listId,
                                                  toDoListTitle: displayName,
                                                  __parent: todo,
                                                },
                                                task,
                                              )
                                            }
                                            aria-label="Open checklist details"
                                          >
                                            <div>
                                              {" "}
                                              <InfoButton />
                                            </div>
                                          </button>

                                          <button
                                            type="button"
                                            className="delete-inline-btn"
                                            onClick={() =>
                                              setDeleteTarget({
                                                type: "checklist",
                                                id: listId,
                                              })
                                            }
                                            aria-label="Delete checklist"
                                          >
                                            <DeleteButton size={40} />
                                          </button>
                                          <AddButton
                                            size={28}
                                            text={null}
                                            tooltip="Add checklist item"
                                            ariaLabel={`Show add checklist input for todo ${todoId}`}
                                            onClick={() => {
                                              // show the inline checklist input for this todo
                                              setChecklistVisible((s) => ({
                                                ...s,
                                                [todoId]: true,
                                              }));
                                              // focus the input next tick
                                              setTimeout(() => {
                                                document
                                                  .getElementById(
                                                    `inline-checklist-${todoId}`,
                                                  )
                                                  ?.focus?.();
                                              }, 20);
                                            }}
                                          />
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* Details modal */}
      {selectedItem && (
        <DetailsModal
          item={selectedItem}
          currentUserRole={currentUserRole}
          currentUserId={currentUserId}
          onDeleteTodoList={onDeleteTodoList}
          onUpdateTodoList={onUpdateTodoList}
          projectMembers={projectMembers}
          parentTaskOwnerId={
            selectedItem.parentTaskOwnerId ??
            selectedItem.parentTask?.ownerId ??
            selectedItem.taskOwnerId ??
            selectedItem.task?.ownerId ??
            null
          }
          onClose={() => setSelectedItem(null)}
          // DetailsModal onSave handler — replace the current inline one
          onSave={async (item, formData) => {
            console.log(
              "%c[TABLEVIEW] onSave called",
              "color:#0af; font-weight:bold;",
              { item, formData },
            );

            // Build patch same as you already do
            const patch = {
              taskTitle: formData.taskTitle ?? formData.title ?? undefined,
              description: formData.description ?? undefined,
              remark: formData.remark ?? undefined,
              priority: formData.priority ?? undefined,
              startDate: formData.startDate ?? undefined,
              endDate: formData.endDate ?? undefined,
              schedulingMode: formData.schedulingMode ?? undefined,
              durationDays: formData.durationDays ?? undefined,
              accessId:
                formData.accessId ?? formData.access?.accessId ?? undefined,
              assignedEmpId:
                formData.assignedEmpId ??
                formData.assignedAssignedEmpId ??
                formData.assignedEmp?.empId ??
                undefined,
              taskOwnerId: formData.taskOwnerId ?? undefined,
            };

            // cleanup undefined
            Object.keys(patch).forEach(
              (k) => patch[k] === undefined && delete patch[k],
            );

            try {
              // TASK update
              if (item?.taskId) {
                // call handler (should return updated task or at least success)
                const res = await handleUpdateTask(item.taskId, patch);

                // if handler returns updated task object (common pattern), merge into state
                if (res && (res.data || res.updated || res)) {
                  // try to extract best candidate for updated task
                  const updated =
                    (res.data &&
                      (Array.isArray(res.data) ? res.data[0] : res.data)) ||
                    res.updated ||
                    res;

                  setTasks((prev) =>
                    (prev || []).map((t) =>
                      Number(t.taskId ?? t.id) === Number(item.taskId)
                        ? { ...t, ...updated }
                        : t,
                    ),
                  );
                } else {
                  // fallback: optimistically patch fields locally
                  setTasks((prev) =>
                    (prev || []).map((t) =>
                      Number(t.taskId ?? t.id) === Number(item.taskId)
                        ? { ...t, ...patch }
                        : t,
                    ),
                  );
                }
                return res;
              }

              // TODO update
              if (item?.toDoId) {
                const todoPatch = {
                  toDoTitle:
                    patch.taskTitle ?? formData.title ?? formData.toDoTitle,
                  remark: patch.remark,
                  accessId: patch.accessId,
                  assignedEmpId: patch.assignedEmpId,
                };

                const res = await handleUpdateTodo(item.toDoId, todoPatch);

                if (res && (res.data || res.updated || res)) {
                  const updated =
                    (res.data &&
                      (Array.isArray(res.data) ? res.data[0] : res.data)) ||
                    res.updated ||
                    res;

                  // merge into tasks -> find parent task and replace the todo
                  setTasks((prev) =>
                    (prev || []).map((t) => ({
                      ...t,
                      todoRequestDTOList: (t.todoRequestDTOList || []).map(
                        (td) =>
                          Number(td.toDoId ?? td.id) === Number(item.toDoId)
                            ? { ...td, ...updated }
                            : td,
                      ),
                    })),
                  );
                } else {
                  // optimistic local merge
                  setTasks((prev) =>
                    (prev || []).map((t) => ({
                      ...t,
                      todoRequestDTOList: (t.todoRequestDTOList || []).map(
                        (td) =>
                          Number(td.toDoId ?? td.id) === Number(item.toDoId)
                            ? { ...td, ...todoPatch }
                            : td,
                      ),
                    })),
                  );
                }
                return res;
              }

              // Checklist fallback: let parent handler deal with it
              if (item?.toDoListId ?? item?.id) {
                const listId = item.toDoListId ?? item.id;
                const res =
                  onUpdateTodoList &&
                  (await onUpdateTodoList(listId, formData));
                // update local state if response contains updated list
                if (res && (res.data || res.updated || res)) {
                  const updated = res.data || res.updated || res;
                  setTasks((prev) =>
                    (prev || []).map((t) => ({
                      ...t,
                      todoRequestDTOList: (t.todoRequestDTOList || []).map(
                        (td) => ({
                          ...td,
                          toDoListResponseDTOList: (
                            td.toDoListResponseDTOList || []
                          ).map((li) =>
                            Number(li.toDoListId ?? li.id) === Number(listId)
                              ? { ...li, ...updated }
                              : li,
                          ),
                        }),
                      ),
                    })),
                  );
                }
                return res;
              }
            } catch (err) {
              // keep original behavior (log + show toast if exist)
              console.error("[TableView onSave] update failed:", err);
              // Option: call refreshProject to re-sync
              if (typeof refreshProject === "function") {
                try {
                  await refreshProject(project?.projectId);
                } catch (e) {
                  console.error("refreshProject failed after onSave error:", e);
                }
              }
              throw err;
            }
          }}
        />
      )}

      {/* Add Todo Modal */}
      <AddTodoModal
        open={todoModal.open}
        onClose={() =>
          setTodoModal({ open: false, taskId: null, stagePositionId: null })
        }
        taskId={todoModal.taskId}
        stagePositionId={todoModal.stagePositionId}
        onCreateTodo={onCreateTodo}
        accessId={currentAccessId}
        projectMembers={projectMembers}
        currentUserRole={currentUserRole}
      />

      {/* Add Checklist Modal */}
      <AddChecklistModal
        open={checkModal.open}
        onClose={() => setCheckModal({ open: false, toDoId: null })}
        toDoId={checkModal.toDoId}
        onCreateTodoList={onCreateTodoList}
      />

      {/* Toast */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: "", type: "info" })}
        />
      )}
      {/* Stage change remark prompt */}
      {stageRemarkPrompt && (
        <div
          className="stage-remark-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) cancelStageRemark();
          }}
        >
          <div className="stage-remark-modal">
            <h4>Add a remark</h4>
            <p className="stage-remark-sub">
              {stageRemarkPrompt.kind === "task"
                ? `Moving to "${stageRemarkPrompt.selectedLabel || "new stage"}"`
                : "Updating ToDo stage"}
            </p>
            <textarea
              autoFocus
              rows={3}
              placeholder="Optional remark for this stage change…"
              value={stageRemarkText}
              onChange={(e) => setStageRemarkText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  confirmStageRemark();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelStageRemark();
                }
              }}
            />
            <div className="stage-remark-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={cancelStageRemark}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmStageRemark}
              >
                Save stage
              </button>
            </div>
          </div>
        </div>
      )}

      {checklistRemarkPrompt && (
        <div
          className="stage-remark-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) cancelChecklistRemarkPrompt();
          }}
        >
          <div className="stage-remark-modal">
            <h4>
              {checklistRemarkPrompt.completion
                ? "Edit remark"
                : "Add a remark"}
            </h4>
            <p className="stage-remark-sub">
              {checklistRemarkPrompt.title || "Checklist item"}
            </p>
            <textarea
              autoFocus
              rows={3}
              placeholder="Remark for this checklist item…"
              value={checklistRemarkText}
              onChange={(e) => setChecklistRemarkText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  confirmChecklistRemarkPrompt();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelChecklistRemarkPrompt();
                }
              }}
            />
            <div className="stage-remark-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={cancelChecklistRemarkPrompt}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmChecklistRemarkPrompt}
              >
                Save remark
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <DeleteWarningModal
          message="Are you sure you want to delete this item? This cannot be undone."
          type="warning"
          onConfirm={() => {
            const { type, id } = deleteTarget;
            if (type === "task" && onDeleteTask) onDeleteTask(id);
            if (type === "todo" && onDeleteTodo) onDeleteTodo(id);
            if (type === "checklist" && onDeleteTodoList) onDeleteTodoList(id);
            setDeleteTarget(null);
          }}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
